import React, { useState, useEffect, useRef } from 'react';
import './RentalsPage.css';
import { authHeader } from '../services/authService';

const companyLogo = process.env.PUBLIC_URL + '/Logo_horizontal_fondnoir-_1_.webp';

function AdminRentalsPage() {
  const [rentals, setRentals] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loading2, setLoading2] = useState(true);
  // Pour la modale de détails
  const [selectedRental, setSelectedRental] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  
  // Pour la modale de refus
  const [isRefusalModalOpen, setIsRefusalModalOpen] = useState(false);
  const [refusalMessage, setRefusalMessage] = useState('');
  
  // Pour la modale de devis
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Références pour l'impression du devis
  const quoteRef = useRef(null);

  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  
  // Référence pour l'impression de la facture
  const invoiceRef = useRef(null);
  const [quoteEmailMessage, setQuoteEmailMessage] = useState('');
  const [invoiceEmailMessage, setInvoiceEmailMessage] = useState('');

  const [washingPrice, setWashingPrice] = useState(20);
  const [showDeposit, setShowDeposit] = useState(true);
  
  // Informations de l'entreprise
  const companyInfo = {
    name: "Ouest Pro Event",
    address: "4 Rue de L'argoat",
    zipCode: "22200",
    city: "Saint-Agathon",
    phone: "06.76.98.99.89",
    email: "oceane.legrand@ouest-proevent.fr / theo.cioclei@ouest-proevent.fr",
    siret: "123 456 789 00012",
    tvaRate: 20
  };
  
  // État pour la livraison
  const [deliveryDistance, setDeliveryDistance] = useState(0);
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  
  // Fonction pour obtenir les actions disponibles selon le statut
  const getAvailableActions = (status) => {
    switch (status.toLowerCase()) {
      case 'en attente':
        return {
          canSendQuote: true,
          canRefuse: true,
          canSendInvoice: false
        };
      case 'devis envoyé par mail':
        return {
          canSendQuote: false,
          canRefuse: true,
          canSendInvoice: false
        };
      case 'paid':
        return {
          canSendQuote: false,
          canRefuse: false,
          canSendInvoice: true
        };
      default:
        return {
          canSendQuote: false,
          canRefuse: false,
          canSendInvoice: false
        };
    }
  };

  // Fonction pour calculer la distance et le prix de livraison avec OpenStreetMap
  const calculateDistanceAndPrice = async (rental) => {
    // Si ce n'est pas une livraison ou si la distance est déjà définie, ne rien faire
    if (rental.delivery_method !== 'delivery' || 
        (rental.delivery_distance && rental.delivery_distance > 0)) {
      return;
    }

    try {
      // 1. Géocodage de l'adresse de l'entreprise avec Nominatim
      const companyAddress = `${companyInfo.address}, ${companyInfo.zipCode} ${companyInfo.city}, France`;
      const companyResponse = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(companyAddress)}`);
      const companyData = await companyResponse.json();
      
      if (!companyData.length) {
        console.error("Adresse de l'entreprise introuvable");
        return null;
      }
      
      // 2. Géocodage de l'adresse de livraison
      const deliveryAddress = `${rental.delivery_street}, ${rental.delivery_zip_code} ${rental.delivery_city}, France`;
      const deliveryResponse = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(deliveryAddress)}`);
      const deliveryData = await deliveryResponse.json();
      
      if (!deliveryData.length) {
        console.error("Adresse de livraison introuvable");
        return null;
      }
      
      // 3. Récupérer les coordonnées
      const companyCoords = {
        lon: companyData[0].lon,
        lat: companyData[0].lat
      };
      
      const deliveryCoords = {
        lon: deliveryData[0].lon,
        lat: deliveryData[0].lat
      };
      
      // 4. Utiliser OSRM pour calculer l'itinéraire et la distance
      const osrmResponse = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${companyCoords.lon},${companyCoords.lat};${deliveryCoords.lon},${deliveryCoords.lat}?overview=false`
      );
      
      const routeData = await osrmResponse.json();
      
      if (routeData.code !== 'Ok' || !routeData.routes.length) {
        console.error("Impossible de calculer l'itinéraire");
        return null;
      }
      
      // 5. Récupérer la distance en mètres et convertir en kilomètres
      const distanceInMeters = routeData.routes[0].distance;
      const distanceInKm = Math.ceil(distanceInMeters / 1000); // Arrondir au km supérieur
      
      // 6. Calculer le prix (1.50€ par km aller-retour)
      const pricePerKm = 1.50;
      const deliveryPrice = distanceInKm * 2 * pricePerKm; // × 2 pour l'aller-retour
      
      // Mettre à jour les états
      setDeliveryDistance(distanceInKm);
      setDeliveryPrice(deliveryPrice);
      
      // Mettre à jour les données du devis avec le nouveau prix total
      const totalHT = quoteData.subtotal + deliveryPrice + (quoteData.washingOption ? quoteData.washingPrice : 0);
      const tvaAmount = totalHT * (companyInfo.tvaRate / 100);
      const totalTTC = totalHT + tvaAmount;
      
      setQuoteData({
        ...quoteData,
        deliveryPrice: deliveryPrice,
        totalPrice: totalTTC
      });
      
      return { distance: distanceInKm, price: deliveryPrice };
      
    } catch (error) {
      console.error("Erreur lors du calcul de la distance:", error);
      return null;
    }
  };

  const calculateDeliveryPrice = (distance) => {
    // Prix par kilomètre, multiplié par 2 pour l'aller-retour
    return distance * 2 * 2; // 2€ par km × 2 (aller-retour)
  };
  


  // Récupérer la liste des locations paginée
const fetchRentals = async (pageNumber = 1) => {
  try {
    console.log(`https://backend.ouest-pro-event.fr/admin/rentals?page=${pageNumber}&per_page=15`);
    const res = await fetch(`https://backend.ouest-pro-event.fr/admin/rentals?page=${pageNumber}&per_page=15`, {
      headers: {
        ...authHeader() // Ajout de l'en-tête d'authentification
      }
    });
    
    if (!res.ok) {
      // Si le statut est 401 (non autorisé), cela peut signifier que le token a expiré
      if (res.status === 401) {
        throw new Error("Session expirée, veuillez vous reconnecter");
      }
      throw new Error("Erreur lors de la récupération des locations");
    }
    
    const data = await res.json();
    setRentals(data.rentals);
    setTotalPages(data.pages);
    setLoading(false);
  } catch (error) {
    console.error("Erreur lors de la récupération des locations:", error);
    // setError(error.message || "Erreur lors de la récupération des locations");
    setLoading(false);
  }
};

  useEffect(() => {
    fetchRentals(page);
  }, [page]);

  // Fonction pour ouvrir la modale de détails
  const openDetailsModal = (rental) => {
    setSelectedRental(rental);
    setIsDetailsModalOpen(true);
  };

  // Fonction pour ouvrir la modale de devis
  const openQuoteModal = (rental) => {
    // Calculer le nombre de jours de location (incluant la date de fin)
    const startDate = new Date(rental.start_date);
    const endDate = new Date(rental.end_date);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    // Définir la distance de livraison par défaut
    let initialDeliveryDistance = 0;
    let initialDeliveryPrice = 0;
    
    if (rental.delivery_distance && rental.delivery_distance > 0) {
      // Si la distance est déjà définie, l'utiliser
      initialDeliveryDistance = rental.delivery_distance;
      initialDeliveryPrice = rental.delivery_price || calculateDeliveryPrice(initialDeliveryDistance);
    }
    
    // Préparer les données du devis - MODIFIÉ: suppression du calcul avec les jours
    // const subtotal = rental.items.reduce((sum, item) => 
    //   sum + (item.quantity * item.daily_price), 0);

    const services = rental.services || [];
    const productsSubtotal = rental.items.reduce((sum, item) =>
      sum + (item.quantity * item.daily_price), 0);
    const servicesSubtotal = services.reduce((sum, service) =>
      sum + (service.quantity * (service.unit_price || 0)), 0);
    const subtotal = productsSubtotal + servicesSubtotal;


    // Vérifier si l'option de lavage est activée
    const washingOption = rental.washing_option || false;
    const rentalWashingPrice = rental.washing_price || washingPrice;

    // Calculer le total HT avec livraison et option lavage
    const totalHT = subtotal + initialDeliveryPrice + (washingOption ? rentalWashingPrice : 0);

    // Calculer la TVA
    const tvaAmount = totalHT * (companyInfo.tvaRate / 100);

    // Calculer le total TTC
    const totalTTC = totalHT + tvaAmount;

    const quoteNumber = rental.quote_number || `D-${rental.account_id}-${new Date().toISOString().split('T')[0].replace(/-/g, '')}-001`;

    
    const quoteData = {
      id: rental.id,
      quoteNumber: quoteNumber,
      clientName: `${rental.first_name} ${rental.last_name}`,
      email: rental.email,
      deliveryMethod: rental.delivery_method,
      deliveryAddress: rental.delivery_method === 'delivery' ? {
        street: rental.delivery_street,
        city: rental.delivery_city,
        zipCode: rental.delivery_zip_code
      } : null,
      startDate: rental.start_date,
      endDate: rental.end_date,
      items: rental.items.map(item => ({
        ...item,
        subtotal: item.quantity * item.daily_price  // MODIFIÉ: suppression du calcul avec les jours
      })),
      services: services.map(service => ({
        ...service,
        name: service.name || service.formula_item_title || service.formula_name || 'Prestation',
        subtotal: service.quantity * (service.unit_price || 0)
      })),
      days: diffDays,
      subtotal: subtotal,
      deliveryPrice: initialDeliveryPrice,
      washingOption: washingOption,
      washingPrice: rentalWashingPrice,
      tvaAmount: tvaAmount,
      totalPrice: totalTTC
    };
    
    // Mettre à jour les états et ouvrir la modale
    setDeliveryDistance(initialDeliveryDistance);
    setDeliveryPrice(initialDeliveryPrice);
    setQuoteData(quoteData);
    setIsQuoteModalOpen(true);
    
    // Si c'est une livraison et que la distance n'est pas encore définie, la calculer
    if (rental.delivery_method === 'delivery' && 
        (!rental.delivery_distance || rental.delivery_distance <= 0)) {
      
      // Activer l'indicateur de chargement
      setLoading2(true);
      
      // Calculer la distance après un délai pour respecter les limites de l'API
      setTimeout(() => {
        calculateDistanceAndPrice(rental, quoteData).then(result => {
          // Désactiver l'indicateur de chargement
          setLoading2(false);
          
          if (result) {
            // Mise à jour des états avec les nouvelles valeurs
            setDeliveryDistance(result.distance);
            setDeliveryPrice(result.price);
            
            // Recalcul du total avec la nouvelle distance et l'option lavage
            const newTotalHT = subtotal + result.price + (rental.washingOption ? rental.washingPrice : 0);
            const newTvaAmount = newTotalHT * (companyInfo.tvaRate / 100);
            const newTotalTTC = newTotalHT + newTvaAmount;
            
            // Mise à jour du devis
            setQuoteData({
              ...quoteData,
              deliveryPrice: result.price,
              tvaAmount: newTvaAmount,
              totalPrice: newTotalTTC
            });
          } else {
            // Si le calcul a échoué, proposer de saisir manuellement
            alert("Le calcul automatique de la distance a échoué. Veuillez saisir manuellement la distance.");
            setIsEditMode(true);
          }
        });
      }, 1000); // Délai d'1 seconde
    }
  };

 

  // Fonction pour ouvrir la modale de facture
const openInvoiceModal = (rental) => {
  console.log('rental : ', rental);
  
  // Calculer le nombre de jours de location (incluant la date de fin)
  const startDate = new Date(rental.start_date);
  const endDate = new Date(rental.end_date);
  const diffTime = Math.abs(endDate - startDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  
  // Définir la distance de livraison
  let deliveryDistance = rental.delivery_distance || 0;
  const deliveryPrice = rental.delivery_price || calculateDeliveryPrice(deliveryDistance);
  
  // Vérifier si l'option de lavage est activée
  const washingOption = rental.washing_option || false;
  const rentalWashingPrice = rental.washing_price || washingPrice;

  // Préparer les données de la facture - MODIFIÉ: suppression du calcul avec les jours
  // const subtotal = rental.items.reduce((sum, item) => 
  //   sum + (item.quantity * item.daily_price), 0);

  const invoiceServices = rental.services || [];
  const invoiceProductsSubtotal = rental.items.reduce((sum, item) =>
    sum + (item.quantity * item.daily_price), 0);
  const invoiceServicesSubtotal = invoiceServices.reduce((sum, service) =>
    sum + (service.quantity * (service.unit_price || 0)), 0);
  const subtotal = invoiceProductsSubtotal + invoiceServicesSubtotal;
  
  // Calculer le total HT avec livraison et option lavage
  const totalHT = subtotal + deliveryPrice + (washingOption ? rentalWashingPrice : 0);
  
  // Calculer la TVA
  const tvaAmount = totalHT * (companyInfo.tvaRate / 100);
  
  // Calculer le total TTC
  const totalTTC = totalHT + tvaAmount;

  const invoiceNumber = rental.quote_number 
  ? rental.quote_number.replace('D-', 'F-') 
  : `F-${rental.account_id}-${new Date().toISOString().split('T')[0].replace(/-/g, '')}-001`;

  
  const invoiceData = {
    id: rental.id,
    invoiceNumber: invoiceNumber,
    clientName: `${rental.first_name} ${rental.last_name}`,
    email: rental.email,
    deliveryMethod: rental.delivery_method,
    deliveryAddress: rental.delivery_method === 'delivery' ? {
      street: rental.delivery_street,
      city: rental.delivery_city,
      zipCode: rental.delivery_zip_code
    } : null,
    startDate: rental.start_date,
    endDate: rental.end_date,
    items: rental.items.map(item => ({
      ...item,
      subtotal: item.quantity * item.daily_price  // MODIFIÉ: suppression du calcul avec les jours
    })),
    services: invoiceServices.map(service => ({
      ...service,
      name: service.name || service.formula_item_title || service.formula_name || 'Prestation',
      subtotal: service.quantity * (service.unit_price || 0)
    })),
    days: diffDays,
    subtotal: subtotal,
    deliveryPrice: deliveryPrice,
    deliveryDistance: deliveryDistance,
    washingOption: washingOption,
    washingPrice: rentalWashingPrice,
    tvaAmount: tvaAmount,
    totalPrice: totalTTC
  };
  
  setDeliveryDistance(deliveryDistance);
  setDeliveryPrice(deliveryPrice);
  setInvoiceData(invoiceData);
  setIsInvoiceModalOpen(true);
};

  // Fermeture des modales
  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedRental(null);
  };

  // Fermeture de la modale de facture
  const closeInvoiceModal = () => {
    setIsInvoiceModalOpen(false);
    setInvoiceData(null);
    setInvoiceEmailMessage('');
  };

  const closeRefusalModal = () => {
    setIsRefusalModalOpen(false);
    setRefusalMessage('');
  };
  
  const closeQuoteModal = () => {
    setIsQuoteModalOpen(false);
    setQuoteData(null);
    setIsEditMode(false);
    setQuoteEmailMessage(''); // Réinitialiser le message d'email

  };

  // Action pour refuser une location avec message
  const handleRefuseAction = async (rentalId, message) => {
    try {
      const res = await fetch(`https://backend.ouest-pro-event.fr/admin/rentals/${rentalId}/refuse`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message })
      });
      if (res.ok) {
        fetchRentals(page);
        closeRefusalModal();
        closeDetailsModal();
      } else {
        const data = await res.json();
        alert("Erreur : " + data.error);
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi du refus :", error);
      alert("Erreur lors de l'envoi du refus");
    }
  };

  // Fonction utilitaire pour convertir une image en base64
  const getBase64FromImageUrl = async (url) => {
    try {
      // Vérifiez si l'URL est relative ou absolue
      const absoluteUrl = url.startsWith('http') ? url : window.location.origin + url;
      
      const response = await fetch(absoluteUrl, {
        // Ajoutez ces options pour éviter les problèmes CORS
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Erreur lors de la conversion de l'image en base64:", error);
      // Au lieu de retourner null, retournez une URL de secours
      console.log("Utilisation d'une URL de secours pour le logo");
      return process.env.PUBLIC_URL + '/Logo_horizontal_fondnoir-_1_.webp';
    }
  };

// Fonction pour générer et envoyer le devis par email
const handleGenerateAndSendQuote = async (rentalId) => {
  try {
    if (quoteData.deliveryMethod === 'delivery' && deliveryPrice <= 0) {
      alert("Attention: Le prix de livraison est à 0€. Veuillez saisir un tarif kilométrique avant d'envoyer le devis.");
      return;
    }
    
    // Traiter spécifiquement le logo
    let logoSrc = '';
    try {
      const logoUrl = process.env.PUBLIC_URL + '/Logo_horizontal_fondnoir-_1_.webp';
      const base64Logo = await getBase64FromImageUrl(logoUrl);
      if (base64Logo) {
        logoSrc = base64Logo;
      }
    } catch (error) {
      console.error("Erreur lors du chargement du logo:", error);
    }
    
    // Extraire les données des articles
    const items = quoteData.items.map(item => {
      const itemTvaAmount = item.subtotal * (companyInfo.tvaRate / 100);
      const itemTotalTTC = item.subtotal + itemTvaAmount;
      
      return {
        name: item.product_name,
        quantity: item.quantity,
        unitPrice: item.daily_price.toFixed(2),
        totalHT: item.subtotal.toFixed(2),
        tva: itemTvaAmount.toFixed(2),
        totalTTC: itemTotalTTC.toFixed(2)
      };
    });

    (quoteData.services || []).forEach(service => {
      const serviceSubtotal = service.subtotal || 0;
      const serviceTvaAmount = serviceSubtotal * (companyInfo.tvaRate / 100);
      const serviceName = service.name || service.formula_item_title || service.formula_name || 'Prestation';
      const label = service.formula_name ? `${serviceName} (${service.formula_name})` : serviceName;

      items.push({
        name: label,
        quantity: (service.quantity ?? 0).toString(),
        unitPrice: (service.unit_price || 0).toFixed(2),
        totalHT: serviceSubtotal.toFixed(2),
        tva: serviceTvaAmount.toFixed(2),
        totalTTC: (serviceSubtotal + serviceTvaAmount).toFixed(2)
      });
    });

    (quoteData.services || []).forEach(service => {
      const serviceSubtotal = (service.subtotal || 0);
      const serviceTvaAmount = serviceSubtotal * (companyInfo.tvaRate / 100);
      const serviceName = service.name || service.formula_item_title || 'Prestation';
      const label = service.formula_name ? `${serviceName} (${service.formula_name})` : serviceName;

      items.push({
        name: label,
        quantity: (service.quantity ?? 0).toString(),
        unitPrice: (service.unit_price || 0).toFixed(2),
        totalHT: serviceSubtotal.toFixed(2),
        tva: serviceTvaAmount.toFixed(2),
        totalTTC: (serviceSubtotal + serviceTvaAmount).toFixed(2)
      });
    });
    
    // Ajouter la ligne de livraison si applicable
    if (quoteData.deliveryMethod === 'delivery' && deliveryPrice > 0) {
      const deliveryTVA = deliveryPrice * (companyInfo.tvaRate / 100);
      items.push({
        name: `Frais de livraison aller-retour (${deliveryDistance} km)`,
        quantity: '2',
        unitPrice: (deliveryPrice / 2).toFixed(2), // Prix par trajet, et non par km
        totalHT: deliveryPrice.toFixed(2),
        tva: deliveryTVA.toFixed(2),
        totalTTC: (deliveryPrice + deliveryTVA).toFixed(2)
      });
    }
    
    // Ajouter l'option lavage si applicable
    if (quoteData.washingOption) {
      const washingTVA = quoteData.washingPrice * (companyInfo.tvaRate / 100);
      items.push({
        name: "Forfait lavage",
        quantity: '1',
        unitPrice: quoteData.washingPrice.toFixed(2),
        totalHT: quoteData.washingPrice.toFixed(2),
        tva: washingTVA.toFixed(2),
        totalTTC: (quoteData.washingPrice + washingTVA).toFixed(2)
      });
    }
    
    // Calculer les totaux
    const subtotalHT = quoteData.subtotal + deliveryPrice + (quoteData.washingOption ? quoteData.washingPrice : 0);
    const totalTVA = subtotalHT * (companyInfo.tvaRate / 100);
    const totalTTC = subtotalHT + totalTVA;
    const deposit = totalTTC * 0.5;

    
    // Générer les lignes d'articles HTML
    let itemsHtml = '';
    items.forEach(item => {
      itemsHtml += `
        <tr>
          <td>${item.name}</td>
          <td class="text-center">${item.quantity}</td>
          <td class="text-right">${item.unitPrice} €</td>
          <td class="text-right">${item.totalHT} €</td>
          <td class="text-right">${item.tva} €</td>
          <td class="text-right">${item.totalTTC} €</td>
        </tr>
      `;
    });
    
    // Créer le HTML complet avec une structure optimisée pour l'impression
    const completeHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Devis ${quoteData.quoteNumber}</title>
        <style>
          @page { 
            size: A4; 
            margin: 1cm;
          }
          
          body { 
            font-family: Arial, sans-serif;
            color: #333;
            line-height: 1.4;
            margin: 0;
            padding: 0;
          }
          
          .quote-preview {
            max-width: 800px;
            margin: 0 auto;
            padding: 0;
          }
          
          .quote-header-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 1px solid #ddd;
            page-break-inside: avoid;
          }
          
          .logo-container {
            width: 100px;
            min-width: 100px;
            margin-right: 15px;
          }
          
          .company-logo {
            max-width: 100%;
            height: auto;
            object-fit: contain;
          }
          
          .company-details {
            flex-grow: 1;
            min-width: 200px;
          }
          
          .company-details h3 {
            margin-top: 0;
            margin-bottom: 5px;
          }
          
          .company-details p {
            margin: 2px 0;
          }
          
          .quote-reference {
            min-width: 180px;
            text-align: right;
          }
          
          .quote-reference h2 {
            color: #C9A77C;
            font-size: 28px;
            margin-top: 0;
            margin-bottom: 10px;
          }
          
          .quote-reference p {
            margin: 3px 0;
          }
          
          .quote-client-info {
            background-color: #f8f8f8;
            padding: 10px 15px;
            border-radius: 4px;
            margin-bottom: 10px;
            page-break-inside: avoid;
          }
          
          .quote-client-info h3 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #444;
            font-size: 16px;
          }
          
          .quote-client-info p {
            margin: 3px 0;
          }
          
          .quote-details {
            margin-top: 10px;
            page-break-inside: avoid;
          }
          
          .quote-details h3 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #444;
            font-size: 16px;
          }
          
          .quote-details p {
            margin: 3px 0;
          }
          
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0 15px 0;
          }
          
          .items-table th {
            background-color: #444;
            color: white;
            padding: 8px 10px;
            text-align: left;
            font-size: 14px;
          }
          
          .items-table td {
            padding: 6px 10px;
            border-bottom: 1px solid #ddd;
            font-size: 13px;
            vertical-align: middle;
          }
          
          .items-table thead {
            display: table-header-group;
          }
          
          .items-table tbody {
            display: table-row-group;
          }
          
          .total-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
            page-break-inside: avoid;
          }
          
          .total-table td {
            padding: 8px 10px;
            font-size: 14px;
          }
          
          .total-table .total-label {
            text-align: right;
            font-weight: bold;
          }
          
          .total-table .total-value {
            text-align: right;
            width: 120px;
          }
          
          .total-row {
            background-color: #f0f0f0;
          }
          
          .deposit-row {
            color: #C9A77C;
          }
          
          .quote-terms {
            margin-top: 15px;
            padding: 10px 15px;
            background-color: #f8f8f8;
            border-radius: 4px;
            page-break-inside: avoid;
          }
          
          .quote-terms h3 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #444;
            font-size: 16px;
          }
          
          .quote-terms p {
            margin: 5px 0;
            font-size: 13px;
          }
          
          .text-right {
            text-align: right;
          }
          
          .text-center {
            text-align: center;
          }
          
          .delivery-row td, .washing-option-row td {
            background-color: #fafafa;
          }
        </style>
      </head>
      <body>
        <div class="quote-preview">
          <div class="quote-header-section">
            <div class="logo-container">
              <img src="${logoSrc}" alt="Logo" class="company-logo" />
            </div>
            <div class="company-details">
              <h3>${companyInfo.name}</h3>
              <p>${companyInfo.address}</p>
              <p>${companyInfo.zipCode} ${companyInfo.city}</p>
              <p>Tél: ${companyInfo.phone}</p>
              <p>Email: ${companyInfo.email}</p>
              <p>SIRET: ${companyInfo.siret}</p>
            </div>
            <div class="quote-reference">
              <h2>DEVIS</h2>
              <p><strong>N°:</strong> ${quoteData.quoteNumber}</p>
              <p><strong>Date:</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
              <p><strong>Validité:</strong> 30 jours</p>
            </div>
          </div>
          
          <div class="quote-client-info">
            <h3>Client</h3>
            <p><strong>Nom:</strong> ${quoteData.clientName}</p>
            <p><strong>Email:</strong> ${quoteData.email}</p>
            ${quoteData.deliveryMethod === 'delivery' && quoteData.deliveryAddress ? `
              <p><strong>Adresse de livraison:</strong></p>
              <p>${quoteData.deliveryAddress.street}</p>
              <p>${quoteData.deliveryAddress.zipCode} ${quoteData.deliveryAddress.city}</p>
            ` : ''}
          </div>
          
          <div class="quote-details">
            <h3>Détails du devis</h3>
            <p><strong>Période de location:</strong> ${new Date(quoteData.startDate).toLocaleDateString('fr-FR')} au ${new Date(quoteData.endDate).toLocaleDateString('fr-FR')}</p>
            <p><strong>Durée:</strong> ${quoteData.days || 1} jour${(quoteData.days || 1) > 1 ? 's' : ''}</p>
          </div>
          
          <!-- Tableau des articles avec en-tête répété sur chaque page -->
          <table class="items-table">
            <thead>
              <tr>
                <th>Désignation</th>
                <th>Quantité</th>
                <th>Prix unitaire</th>
                <th>Total HT</th>
                <th>TVA (${companyInfo.tvaRate}%)</th>
                <th>Total TTC</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>
          
          <!-- Tableau séparé pour les totaux qui ne s'affichera qu'une fois à la fin -->
          <table class="total-table">
            <tr>
              <td class="total-label">Sous-total HT</td>
              <td class="total-value">${subtotalHT.toFixed(2)} €</td>
            </tr>
            <tr>
              <td class="total-label">TVA (${companyInfo.tvaRate}%)</td>
              <td class="total-value">${totalTVA.toFixed(2)} €</td>
            </tr>
            <tr class="total-row">
              <td class="total-label">Total TTC</td>
              <td class="total-value">${totalTTC.toFixed(2)} €</td>
            </tr>
            ${showDeposit ? `
            <tr class="deposit-row">
              <td class="total-label">Acompte à payer (50%)</td>
              <td class="total-value">${deposit.toFixed(2)} €</td>
            </tr>
            ` : ''}
          </table>
          
          <div class="quote-terms">
            <h3>Conditions</h3>
            <p>Ce devis est valable 30 jours à compter de sa date d'émission.</p>
            ${showDeposit ? `
            <p>Un acompte de 50% du montant total est demandé pour confirmer la commande.</p>
            <p>Le solde devra être réglé à la livraison du matériel.</p>
            ` : `
            <p>Le paiement complet sera effectué à la livraison du matériel.</p>
            `}
          </div>
        </div>
      </body>
      </html>
    `;

    console.log("quoteData" , quoteData)
    
    // Envoyer le HTML au backend
    const res = await fetch(`https://backend.ouest-pro-event.fr/admin/rentals/${rentalId}/generate_and_send_quote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        html: completeHtml,
        delivery_distance: deliveryDistance,
        delivery_price: deliveryPrice,
        washing_option: quoteData.washingOption,
        washing_price: quoteData.washingPrice,
        show_deposit: showDeposit, // Ajout de la nouvelle option
        quote_data: {
          ...quoteData,
          washingPrice: quoteData.washingPrice,
          showDeposit: showDeposit // Inclure également dans quoteData
        },
        email_message: quoteEmailMessage
      })
    });
    
    const data = await res.json();
    if (res.ok) {
      
      alert("Le devis a bien été généré et envoyé par email.");
      fetchRentals(page);
      closeQuoteModal();
    } else {
      alert("Erreur : " + data.error);
    }
  } catch (error) {
    console.error("Erreur lors de la génération et de l'envoi du devis :", error);
    alert("Erreur lors de la génération et de l'envoi du devis");
  }
};

const handleGenerateAndSendInvoice = async (rentalId) => {
  try {
    if (invoiceData.deliveryMethod === 'delivery' && deliveryPrice <= 0) {
      alert("Attention: Le prix de livraison est à 0€. Veuillez saisir un tarif kilométrique avant d'envoyer la facture.");
      return;
    }
    
    // Traiter spécifiquement le logo
    let logoSrc = '';
    try {
      const logoUrl = process.env.PUBLIC_URL + '/Logo_horizontal_fondnoir-_1_.webp';
      const base64Logo = await getBase64FromImageUrl(logoUrl);
      if (base64Logo) {
        logoSrc = base64Logo;
      }
    } catch (error) {
      console.error("Erreur lors du chargement du logo:", error);
    }
    
    // Extraire les données des articles
    const items = invoiceData.items.map(item => {
      const itemTvaAmount = item.subtotal * (companyInfo.tvaRate / 100);
      const itemTotalTTC = item.subtotal + itemTvaAmount;
      
      return {
        name: item.product_name,
        quantity: item.quantity,
        unitPrice: item.daily_price.toFixed(2),
        totalHT: item.subtotal.toFixed(2),
        tva: itemTvaAmount.toFixed(2),
        totalTTC: itemTotalTTC.toFixed(2)
      };
    });

    (invoiceData.services || []).forEach(service => {
      const serviceSubtotal = (service.subtotal || 0);
      const serviceTvaAmount = serviceSubtotal * (companyInfo.tvaRate / 100);
      const serviceName = service.name || service.formula_item_title || 'Prestation';
      const label = service.formula_name ? `${serviceName} (${service.formula_name})` : serviceName;

      items.push({
        name: label,
        quantity: (service.quantity ?? 0).toString(),
        unitPrice: (service.unit_price || 0).toFixed(2),
        totalHT: serviceSubtotal.toFixed(2),
        tva: serviceTvaAmount.toFixed(2),
        totalTTC: (serviceSubtotal + serviceTvaAmount).toFixed(2)
      });
    });

    (invoiceData.services || []).forEach(service => {
      const serviceSubtotal = (service.subtotal || 0);
      const serviceTvaAmount = serviceSubtotal * (companyInfo.tvaRate / 100);
      const serviceName = service.name || service.formula_item_title || 'Prestation';
      const label = service.formula_name ? `${serviceName} (${service.formula_name})` : serviceName;

      items.push({
        name: label,
        quantity: (service.quantity ?? 0).toString(),
        unitPrice: (service.unit_price || 0).toFixed(2),
        totalHT: serviceSubtotal.toFixed(2),
        tva: serviceTvaAmount.toFixed(2),
        totalTTC: (serviceSubtotal + serviceTvaAmount).toFixed(2)
      });
    });
    
    // Ajouter la ligne de livraison si applicable
    if (invoiceData.deliveryMethod === 'delivery' && deliveryPrice > 0) {
      const deliveryTVA = deliveryPrice * (companyInfo.tvaRate / 100);
      items.push({
        name: `Frais de livraison aller-retour (${deliveryDistance} km)`,
        quantity: '2',
        unitPrice: (deliveryPrice / 2).toFixed(2), // Prix par trajet, et non par km
        totalHT: deliveryPrice.toFixed(2),
        tva: deliveryTVA.toFixed(2),
        totalTTC: (deliveryPrice + deliveryTVA).toFixed(2)
      });
    }
    
    // Ajouter l'option lavage si applicable
    // Utiliser le prix du forfait de lavage stocké dans invoiceData
    if (invoiceData.washingOption) {
      const invoiceWashingPrice = invoiceData.washingPrice || washingPrice; // Prix stocké ou valeur par défaut
      const washingTVA = invoiceWashingPrice * (companyInfo.tvaRate / 100);
      items.push({
        name: "Forfait lavage",
        quantity: '1',
        unitPrice: invoiceWashingPrice.toFixed(2),
        totalHT: invoiceWashingPrice.toFixed(2),
        tva: washingTVA.toFixed(2),
        totalTTC: (invoiceWashingPrice + washingTVA).toFixed(2)
      });
    }
    
    // Calculer les totaux
    // Utiliser le prix du forfait de lavage stocké dans invoiceData
    const washingCost = invoiceData.washingOption ? (invoiceData.washingPrice || washingPrice) : 0;
    const subtotalHT = invoiceData.subtotal + deliveryPrice + washingCost;
    const totalTVA = subtotalHT * (companyInfo.tvaRate / 100);
    const totalTTC = subtotalHT + totalTVA;
    const deposit = totalTTC * 0.5;
    
    // Générer les lignes d'articles HTML
    let itemsHtml = '';
    items.forEach(item => {
      itemsHtml += `
        <tr>
          <td>${item.name}</td>
          <td class="text-center">${item.quantity}</td>
          <td class="text-right">${item.unitPrice} €</td>
          <td class="text-right">${item.totalHT} €</td>
          <td class="text-right">${item.tva} €</td>
          <td class="text-right">${item.totalTTC} €</td>
        </tr>
      `;
    });
    
    // Créer le HTML complet avec une structure optimisée pour l'impression
    const completeHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Facture ${invoiceData.invoiceNumber}</title>
        <style>
          @page { 
            size: A4; 
            margin: 1cm;
          }
          
          body { 
            font-family: Arial, sans-serif;
            color: #333;
            line-height: 1.4;
            margin: 0;
            padding: 0;
          }
          
          .invoice-preview {
            max-width: 800px;
            margin: 0 auto;
            padding: 0;
          }
          
          .invoice-header-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 1px solid #ddd;
            page-break-inside: avoid;
          }
          
          .logo-container {
            width: 100px;
            min-width: 100px;
            margin-right: 15px;
          }
          
          .company-logo {
            max-width: 100%;
            height: auto;
            object-fit: contain;
          }
          
          .company-details {
            flex-grow: 1;
            min-width: 200px;
          }
          
          .company-details h3 {
            margin-top: 0;
            margin-bottom: 5px;
          }
          
          .company-details p {
            margin: 2px 0;
          }
          
          .invoice-reference {
            min-width: 180px;
            text-align: right;
          }
          
          .invoice-reference h2 {
            color: #C9A77C;
            font-size: 28px;
            margin-top: 0;
            margin-bottom: 10px;
          }
          
          .invoice-reference p {
            margin: 3px 0;
          }
          
          .invoice-client-info {
            background-color: #f8f8f8;
            padding: 10px 15px;
            border-radius: 4px;
            margin-bottom: 10px;
            page-break-inside: avoid;
          }
          
          .invoice-client-info h3 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #444;
            font-size: 16px;
          }
          
          .invoice-client-info p {
            margin: 3px 0;
          }
          
          .invoice-details {
            margin-top: 10px;
            page-break-inside: avoid;
          }
          
          .invoice-details h3 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #444;
            font-size: 16px;
          }
          
          .invoice-details p {
            margin: 3px 0;
          }
          
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0 15px 0;
          }
          
          .items-table th {
            background-color: #444;
            color: white;
            padding: 8px 10px;
            text-align: left;
            font-size: 14px;
          }
          
          .items-table td {
            padding: 6px 10px;
            border-bottom: 1px solid #ddd;
            font-size: 13px;
            vertical-align: middle;
          }
          
          .items-table thead {
            display: table-header-group;
          }
          
          .items-table tbody {
            display: table-row-group;
          }
          
          .total-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
            page-break-inside: avoid;
          }
          
          .total-table td {
            padding: 8px 10px;
            font-size: 14px;
          }
          
          .total-table .total-label {
            text-align: right;
            font-weight: bold;
          }
          
          .total-table .total-value {
            text-align: right;
            width: 120px;
          }
          
          .total-row {
            background-color: #f0f0f0;
          }
          
          .paid-deposit-row {
            color: #27ae60;
          }
          
          .remaining-amount-row {
            color: #C9A77C;
            font-weight: bold;
          }
          
          .invoice-terms {
            margin-top: 15px;
            padding: 10px 15px;
            background-color: #f8f8f8;
            border-radius: 4px;
            page-break-inside: avoid;
          }
          
          .invoice-terms h3 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #444;
            font-size: 16px;
          }
          
          .invoice-terms p {
            margin: 5px 0;
            font-size: 13px;
          }
          
          .text-right {
            text-align: right;
          }
          
          .text-center {
            text-align: center;
          }
          
          .delivery-row td, .washing-option-row td {
            background-color: #fafafa;
          }
        </style>
      </head>
      <body>
        <div class="invoice-preview">
          <div class="invoice-header-section">
            <div class="logo-container">
              <img src="${logoSrc}" alt="Logo" class="company-logo" />
            </div>
            <div class="company-details">
              <h3>${companyInfo.name}</h3>
              <p>${companyInfo.address}</p>
              <p>${companyInfo.zipCode} ${companyInfo.city}</p>
              <p>Tél: ${companyInfo.phone}</p>
              <p>Email: ${companyInfo.email}</p>
              <p>SIRET: ${companyInfo.siret}</p>
            </div>
            <div class="invoice-reference">
              <h2>FACTURE</h2>
              <p><strong>N°:</strong> ${invoiceData.invoiceNumber}</p>
              <p><strong>Date:</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
            </div>
          </div>
          
          <div class="invoice-client-info">
            <h3>Client</h3>
            <p><strong>Nom:</strong> ${invoiceData.clientName}</p>
            <p><strong>Email:</strong> ${invoiceData.email}</p>
            ${invoiceData.deliveryMethod === 'delivery' && invoiceData.deliveryAddress ? `
              <p><strong>Adresse de livraison:</strong></p>
              <p>${invoiceData.deliveryAddress.street}</p>
              <p>${invoiceData.deliveryAddress.zipCode} ${invoiceData.deliveryAddress.city}</p>
            ` : ''}
          </div>
          
          <div class="invoice-details">
            <h3>Détails de la facture</h3>
            <p><strong>Période de location:</strong> ${new Date(invoiceData.startDate).toLocaleDateString('fr-FR')} au ${new Date(invoiceData.endDate).toLocaleDateString('fr-FR')}</p>
            <p><strong>Durée:</strong> ${invoiceData.days || 1} jour${(invoiceData.days || 1) > 1 ? 's' : ''}</p>
          </div>
          
          <!-- Tableau des articles avec en-tête répété sur chaque page -->
          <table class="items-table">
            <thead>
              <tr>
                <th>Désignation</th>
                <th>Quantité</th>
                <th>Prix unitaire</th>
                <th>Total HT</th>
                <th>TVA (${companyInfo.tvaRate}%)</th>
                <th>Total TTC</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>
          
          <!-- Tableau séparé pour les totaux qui ne s'affichera qu'une fois à la fin -->
          <table class="total-table">
            <tr>
              <td class="total-label">Sous-total HT</td>
              <td class="total-value">${subtotalHT.toFixed(2)} €</td>
            </tr>
            <tr>
              <td class="total-label">TVA (${companyInfo.tvaRate}%)</td>
              <td class="total-value">${totalTVA.toFixed(2)} €</td>
            </tr>
            <tr class="total-row">
              <td class="total-label">Total TTC</td>
              <td class="total-value">${totalTTC.toFixed(2)} €</td>
            </tr>
            ${showDeposit ? `
            <tr class="paid-deposit-row">
              <td class="total-label">Acompte déjà payé (50%)</td>
              <td class="total-value">-${deposit.toFixed(2)} €</td>
            </tr>
            <tr class="remaining-amount-row">
              <td class="total-label">Reste à payer</td>
              <td class="total-value">${deposit.toFixed(2)} €</td>
            </tr>
            ` : ''}
          </table>
          
          <div class="invoice-terms">
            <h3>Informations de paiement</h3>
            ${showDeposit ? `
            <p>Un acompte de 50% (${deposit.toFixed(2)} €) a été versé.</p>
            <p>Le solde de ${deposit.toFixed(2)} € est à régler lors de la récupération du matériel.</p>
            ` : `
            <p>Le paiement complet de ${totalTTC.toFixed(2)} € est à effectuer lors de la récupération du matériel.</p>
            `}
            <p>Merci pour votre confiance.</p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    // Envoyer le HTML au backend
    const res = await fetch(`https://backend.ouest-pro-event.fr/admin/rentals/${rentalId}/generate_and_send_invoice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        html: completeHtml,
        delivery_distance: deliveryDistance,
        delivery_price: deliveryPrice,
        washing_option: invoiceData.washingOption,
        washing_price: invoiceData.washingPrice || washingPrice, // Inclure le prix du lavage
        show_deposit: showDeposit, // Ajouter l'option d'affichage de l'acompte
        invoice_data: {
          ...invoiceData,
          showDeposit: showDeposit // Inclure l'option dans les données de facture
        },
        email_message: invoiceEmailMessage 
      })
    });
    
    const data = await res.json();
    if (res.ok) {
      alert("La facture a bien été générée et envoyée par email.");
      fetchRentals(page);
      closeInvoiceModal();
    } else {
      alert("Erreur : " + data.error);
    }

    setInvoiceEmailMessage('');

  } catch (error) {
    console.error("Erreur lors de la génération et de l'envoi de la facture :", error);
    alert("Erreur lors de la génération et de l'envoi de la facture");
  }
};
  
const handleDownloadInvoice = async () => {
  try {
    if (invoiceData.deliveryMethod === 'delivery' && deliveryPrice <= 0) {
      alert("Attention: Le prix de livraison est à 0€. Veuillez saisir un tarif kilométrique avant de télécharger la facture.");
      return;
    }
    
    // Traiter spécifiquement le logo
    let logoSrc = '';
    try {
      const logoUrl = process.env.PUBLIC_URL + '/Logo_horizontal_fondnoir-_1_.webp';
      const base64Logo = await getBase64FromImageUrl(logoUrl);
      if (base64Logo) {
        logoSrc = base64Logo;
      }
    } catch (error) {
      console.error("Erreur lors du chargement du logo:", error);
    }
    
    // Extraire les données des articles
    const items = invoiceData.items.map(item => {
      const itemTvaAmount = item.subtotal * (companyInfo.tvaRate / 100);
      const itemTotalTTC = item.subtotal + itemTvaAmount;
      
      return {
        name: item.product_name,
        quantity: item.quantity,
        unitPrice: item.daily_price.toFixed(2),
        totalHT: item.subtotal.toFixed(2),
        tva: itemTvaAmount.toFixed(2),
        totalTTC: itemTotalTTC.toFixed(2)
      };
    });

    // Ajouter les services (formules) s'ils existent
    (invoiceData.services || []).forEach(service => {
      const serviceSubtotal = parseFloat(service.subtotal || 0);
      const serviceTvaAmount = serviceSubtotal * (companyInfo.tvaRate / 100);
      const serviceUnitPrice = parseFloat(service.unit_price || 0);
      const formulaName = service.formula_name
        || (service.formula && service.formula.name)
        || (service.formula_item && service.formula_item.formula && service.formula_item.formula.name)
        || '';
      const baseName = service.name
        || service.formula_item_title
        || formulaName
        || 'Prestation';
      const label = formulaName && formulaName !== baseName
        ? `${baseName} (${formulaName})`
        : baseName;
      const quantity = service.quantity != null ? service.quantity : 1;

      items.push({
        name: label,
        quantity: quantity.toString(),
        unitPrice: serviceUnitPrice.toFixed(2),
        totalHT: serviceSubtotal.toFixed(2),
        tva: serviceTvaAmount.toFixed(2),
        totalTTC: (serviceSubtotal + serviceTvaAmount).toFixed(2)
      });
    });
    
    // Ajouter la ligne de livraison si applicable
    if (invoiceData.deliveryMethod === 'delivery' && deliveryPrice > 0) {
      const deliveryTVA = deliveryPrice * (companyInfo.tvaRate / 100);
      items.push({
        name: `Frais de livraison aller-retour (${deliveryDistance} km)`,
        quantity: '2',
        unitPrice: (deliveryPrice / 2).toFixed(2), // Prix par trajet, et non par km
        totalHT: deliveryPrice.toFixed(2),
        tva: deliveryTVA.toFixed(2),
        totalTTC: (deliveryPrice + deliveryTVA).toFixed(2)
      });
    }
    
    // Ajouter l'option lavage si applicable
    if (invoiceData.washingOption) {
      // Utiliser le prix du lavage enregistré dans invoiceData
      const invoiceWashingPrice = invoiceData.washingPrice || washingPrice; // Utiliser le prix stocké ou la valeur par défaut
      const washingTVA = invoiceWashingPrice * (companyInfo.tvaRate / 100);
      items.push({
        name: "Forfait lavage",
        quantity: '1',
        unitPrice: invoiceWashingPrice.toFixed(2),
        totalHT: invoiceWashingPrice.toFixed(2),
        tva: washingTVA.toFixed(2),
        totalTTC: (invoiceWashingPrice + washingTVA).toFixed(2)
      });
    }
    
    // // Calculer les totaux
    // // Utiliser le prix du lavage stocké dans invoiceData
    // const washingCost = invoiceData.washingOption ? (invoiceData.washingPrice || washingPrice) : 0;
    // const subtotalHT = invoiceData.subtotal + deliveryPrice + washingCost;
    // const totalTVA = subtotalHT * (companyInfo.tvaRate / 100);
    // const totalTTC = subtotalHT + totalTVA;

    const subtotalHT = items.reduce((sum, item) => sum + parseFloat(item.totalHT), 0);
    const totalTVA = items.reduce((sum, item) => sum + parseFloat(item.tva), 0);
    const totalTTC = items.reduce((sum, item) => sum + parseFloat(item.totalTTC), 0);
    const deposit = totalTTC * 0.5;
    
    // Générer les lignes d'articles HTML
    let itemsHtml = '';
    items.forEach(item => {
      itemsHtml += `
        <tr>
          <td>${item.name}</td>
          <td class="text-center">${item.quantity}</td>
          <td class="text-right">${item.unitPrice} €</td>
          <td class="text-right">${item.totalHT} €</td>
          <td class="text-right">${item.tva} €</td>
          <td class="text-right">${item.totalTTC} €</td>
        </tr>
      `;
    });
    
    // Créer le HTML complet avec une structure optimisée pour l'impression
    const completeHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Facture ${invoiceData.invoiceNumber}</title>
        <style>
          @page { 
            size: A4; 
            margin: 1cm;
          }
          
          body { 
            font-family: Arial, sans-serif;
            color: #333;
            line-height: 1.4;
            margin: 0;
            padding: 0;
          }
          
          .invoice-preview {
            max-width: 800px;
            margin: 0 auto;
            padding: 0;
          }
          
          .invoice-header-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 1px solid #ddd;
            page-break-inside: avoid;
          }
          
          .logo-container {
            width: 100px;
            min-width: 100px;
            margin-right: 15px;
          }
          
          .company-logo {
            max-width: 100%;
            height: auto;
            object-fit: contain;
          }
          
          .company-details {
            flex-grow: 1;
            min-width: 200px;
          }
          
          .company-details h3 {
            margin-top: 0;
            margin-bottom: 5px;
          }
          
          .company-details p {
            margin: 2px 0;
          }
          
          .invoice-reference {
            min-width: 180px;
            text-align: right;
          }
          
          .invoice-reference h2 {
            color: #C9A77C;
            font-size: 28px;
            margin-top: 0;
            margin-bottom: 10px;
          }
          
          .invoice-reference p {
            margin: 3px 0;
          }
          
          .invoice-client-info {
            background-color: #f8f8f8;
            padding: 10px 15px;
            border-radius: 4px;
            margin-bottom: 10px;
            page-break-inside: avoid;
          }
          
          .invoice-client-info h3 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #444;
            font-size: 16px;
          }
          
          .invoice-client-info p {
            margin: 3px 0;
          }
          
          .invoice-details {
            margin-top: 10px;
            page-break-inside: avoid;
          }
          
          .invoice-details h3 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #444;
            font-size: 16px;
          }
          
          .invoice-details p {
            margin: 3px 0;
          }
          
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0 15px 0;
          }
          
          .items-table th {
            background-color: #444;
            color: white;
            padding: 8px 10px;
            text-align: left;
            font-size: 14px;
          }
          
          .items-table td {
            padding: 6px 10px;
            border-bottom: 1px solid #ddd;
            font-size: 13px;
            vertical-align: middle;
          }
          
          .items-table thead {
            display: table-header-group;
          }
          
          .items-table tbody {
            display: table-row-group;
          }
          
          .total-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
            page-break-inside: avoid;
          }
          
          .total-table td {
            padding: 8px 10px;
            font-size: 14px;
          }
          
          .total-table .total-label {
            text-align: right;
            font-weight: bold;
          }
          
          .total-table .total-value {
            text-align: right;
            width: 120px;
          }
          
          .total-row {
            background-color: #f0f0f0;
          }
          
          .paid-deposit-row {
            color: #27ae60;
          }
          
          .remaining-amount-row {
            color: #C9A77C;
            font-weight: bold;
          }
          
          .invoice-terms {
            margin-top: 15px;
            padding: 10px 15px;
            background-color: #f8f8f8;
            border-radius: 4px;
            page-break-inside: avoid;
          }
          
          .invoice-terms h3 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #444;
            font-size: 16px;
          }
          
          .invoice-terms p {
            margin: 5px 0;
            font-size: 13px;
          }
          
          .text-right {
            text-align: right;
          }
          
          .text-center {
            text-align: center;
          }
          
          .delivery-row td, .washing-option-row td {
            background-color: #fafafa;
          }
        </style>
      </head>
      <body>
        <div class="invoice-preview">
          <div class="invoice-header-section">
            <div class="logo-container">
              <img src="${logoSrc}" alt="Logo" class="company-logo" />
            </div>
            <div class="company-details">
              <h3>${companyInfo.name}</h3>
              <p>${companyInfo.address}</p>
              <p>${companyInfo.zipCode} ${companyInfo.city}</p>
              <p>Tél: ${companyInfo.phone}</p>
              <p>Email: ${companyInfo.email}</p>
              <p>SIRET: ${companyInfo.siret}</p>
            </div>
            <div class="invoice-reference">
              <h2>FACTURE</h2>
              <p><strong>N°:</strong> ${invoiceData.invoiceNumber}</p>
              <p><strong>Date:</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
            </div>
          </div>
          
          <div class="invoice-client-info">
            <h3>Client</h3>
            <p><strong>Nom:</strong> ${invoiceData.clientName}</p>
            <p><strong>Email:</strong> ${invoiceData.email}</p>
            ${invoiceData.deliveryMethod === 'delivery' && invoiceData.deliveryAddress ? `
              <p><strong>Adresse de livraison:</strong></p>
              <p>${invoiceData.deliveryAddress.street}</p>
              <p>${invoiceData.deliveryAddress.zipCode} ${invoiceData.deliveryAddress.city}</p>
            ` : ''}
          </div>
          
          <div class="invoice-details">
            <h3>Détails de la facture</h3>
            <p><strong>Période de location:</strong> ${new Date(invoiceData.startDate).toLocaleDateString('fr-FR')} au ${new Date(invoiceData.endDate).toLocaleDateString('fr-FR')}</p>
            <p><strong>Durée:</strong> ${invoiceData.days || 1} jour${(invoiceData.days || 1) > 1 ? 's' : ''}</p>
          </div>
          
          <!-- Tableau des articles avec en-tête répété sur chaque page -->
          <table class="items-table">
            <thead>
              <tr>
                <th>Désignation</th>
                <th>Quantité</th>
                <th>Prix unitaire</th>
                <th>Total HT</th>
                <th>TVA (${companyInfo.tvaRate}%)</th>
                <th>Total TTC</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>
          
          <!-- Tableau séparé pour les totaux qui ne s'affichera qu'une fois à la fin -->
          <table class="total-table">
            <tr>
              <td class="total-label">Sous-total HT</td>
              <td class="total-value">${subtotalHT.toFixed(2)} €</td>
            </tr>
            <tr>
              <td class="total-label">TVA (${companyInfo.tvaRate}%)</td>
              <td class="total-value">${totalTVA.toFixed(2)} €</td>
            </tr>
            <tr class="total-row">
              <td class="total-label">Total TTC</td>
              <td class="total-value">${totalTTC.toFixed(2)} €</td>
            </tr>
            ${showDeposit ? `
            <tr class="paid-deposit-row">
              <td class="total-label">Acompte déjà payé (50%)</td>
              <td class="total-value">-${deposit.toFixed(2)} €</td>
            </tr>
            <tr class="remaining-amount-row">
              <td class="total-label">Reste à payer</td>
              <td class="total-value">${deposit.toFixed(2)} €</td>
            </tr>
            ` : ''}
          </table>
          
          <div class="invoice-terms">
            <h3>Informations de paiement</h3>
            ${showDeposit ? `
            <p>Un acompte de 50% (${deposit.toFixed(2)} €) a été versé.</p>
            <p>Le solde de ${deposit.toFixed(2)} € est à régler lors de la récupération du matériel.</p>
            ` : `
            <p>Le paiement complet de ${totalTTC.toFixed(2)} € est à effectuer lors de la récupération du matériel.</p>
            `}
            <p>Merci pour votre confiance.</p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    // Envoyer la requête pour télécharger le PDF
    const res = await fetch(`https://backend.ouest-pro-event.fr/admin/rentals/${invoiceData.id}/download_invoice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        html: completeHtml,
        delivery_distance: deliveryDistance,
        delivery_price: deliveryPrice,
        washing_option: invoiceData.washingOption,
        washing_price: invoiceData.washingPrice || washingPrice, // Inclure le prix du lavage stocké
        show_deposit: showDeposit, // Ajouter l'option d'affichage d'acompte
        invoice_data: {
          ...invoiceData,
          showDeposit: showDeposit // Inclure l'option dans les données
        }
      })
    });
    
    // Si succès, récupérer le PDF et le télécharger
    if (res.ok) {
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Facture-${invoiceData.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } else {
      const data = await res.json();
      alert("Erreur : " + data.error);
    }
  } catch (error) {
    console.error("Erreur lors du téléchargement de la facture :", error);
    alert("Erreur lors du téléchargement de la facture");
  }
};

// Télécharger le devis en PDF
const handleDownloadQuote = async () => {
  try {
    if (quoteData.deliveryMethod === 'delivery' && deliveryPrice <= 0) {
      alert("Attention: Le prix de livraison est à 0€. Veuillez saisir un tarif kilométrique avant de télécharger le devis.");
      return;
    }
    
    // Traiter spécifiquement le logo
    let logoSrc = '';
    try {
      const logoUrl = process.env.PUBLIC_URL + '/Logo_horizontal_fondnoir-_1_.webp';
      const base64Logo = await getBase64FromImageUrl(logoUrl);
      if (base64Logo) {
        logoSrc = base64Logo;
      }
    } catch (error) {
      console.error("Erreur lors du chargement du logo:", error);
    }
    
    // Extraire les données des articles
    const items = quoteData.items.map(item => {
      const itemTvaAmount = item.subtotal * (companyInfo.tvaRate / 100);
      const itemTotalTTC = item.subtotal + itemTvaAmount;
      
      return {
        name: item.product_name,
        quantity: item.quantity,
        unitPrice: item.daily_price.toFixed(2),
        totalHT: item.subtotal.toFixed(2),
        tva: itemTvaAmount.toFixed(2),
        totalTTC: itemTotalTTC.toFixed(2)
      };
    });

        // Ajouter les services (formules) s'ils existent
        (quoteData.services || []).forEach(service => {
          const serviceSubtotal = parseFloat(service.subtotal || 0);
          const serviceTvaAmount = serviceSubtotal * (companyInfo.tvaRate / 100);
          const serviceUnitPrice = parseFloat(service.unit_price || 0);
          const formulaName = service.formula_name
            || (service.formula && service.formula.name)
            || (service.formula_item && service.formula_item.formula && service.formula_item.formula.name)
            || '';
          const baseName = service.name
            || service.formula_item_title
            || formulaName
            || 'Prestation';
          const label = formulaName && formulaName !== baseName
            ? `${baseName} (${formulaName})`
            : baseName;
          const quantity = service.quantity != null ? service.quantity : 1;
    
          items.push({
            name: label,
            quantity: quantity.toString(),
            unitPrice: serviceUnitPrice.toFixed(2),
            totalHT: serviceSubtotal.toFixed(2),
            tva: serviceTvaAmount.toFixed(2),
            totalTTC: (serviceSubtotal + serviceTvaAmount).toFixed(2)
          });
        });
    
    // Ajouter la ligne de livraison si applicable
    if (quoteData.deliveryMethod === 'delivery' && deliveryPrice > 0) {
      const deliveryTVA = deliveryPrice * (companyInfo.tvaRate / 100);
      items.push({
        name: `Frais de livraison aller-retour (${deliveryDistance} km)`,
        quantity: '2',
        unitPrice: (deliveryPrice / 2).toFixed(2), // Prix par trajet, et non par km
        totalHT: deliveryPrice.toFixed(2),
        tva: deliveryTVA.toFixed(2),
        totalTTC: (deliveryPrice + deliveryTVA).toFixed(2)
      });
    }
    
    // Ajouter l'option lavage si applicable
    if (quoteData.washingOption) {
      const washingTVA = quoteData.washingPrice * (companyInfo.tvaRate / 100);
      items.push({
        name: "Forfait lavage",
        quantity: '1',
        unitPrice: quoteData.washingPrice,
        totalHT: quoteData.washingPrice,
        tva: washingTVA.toFixed(2),
        totalTTC: (quoteData.washingPrice + washingTVA).toFixed(2)
      });
    }
    
    // Calculer les totaux
    // const subtotalHT = quoteData.subtotal + deliveryPrice + (quoteData.washingOption ? quoteData.washingPrice : 0);
    // const totalTVA = subtotalHT * (companyInfo.tvaRate / 100);
    // const totalTTC = subtotalHT + totalTVA;
    const subtotalHT = items.reduce((sum, item) => sum + parseFloat(item.totalHT), 0);
    const totalTVA = items.reduce((sum, item) => sum + parseFloat(item.tva), 0);
    const totalTTC = items.reduce((sum, item) => sum + parseFloat(item.totalTTC), 0);
    const deposit = totalTTC * 0.5;
    
    // Générer les lignes d'articles HTML
    let itemsHtml = '';
    items.forEach(item => {
      itemsHtml += `
        <tr>
          <td>${item.name}</td>
          <td class="text-center">${item.quantity}</td>
          <td class="text-right">${item.unitPrice} €</td>
          <td class="text-right">${item.totalHT} €</td>
          <td class="text-right">${item.tva} €</td>
          <td class="text-right">${item.totalTTC} €</td>
        </tr>
      `;
    });
    
    // Créer le HTML complet avec une structure optimisée pour l'impression
    const completeHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Devis ${quoteData.quoteNumber}</title>
        <style>
          @page { 
            size: A4; 
            margin: 1cm;
          }
          
          body { 
            font-family: Arial, sans-serif;
            color: #333;
            line-height: 1.4;
            margin: 0;
            padding: 0;
          }
          
          .quote-preview {
            max-width: 800px;
            margin: 0 auto;
            padding: 0;
          }
          
          .quote-header-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 1px solid #ddd;
            page-break-inside: avoid;
          }
          
          .logo-container {
            width: 100px;
            min-width: 100px;
            margin-right: 15px;
          }
          
          .company-logo {
            max-width: 100%;
            height: auto;
            object-fit: contain;
          }
          
          .company-details {
            flex-grow: 1;
            min-width: 200px;
          }
          
          .company-details h3 {
            margin-top: 0;
            margin-bottom: 5px;
          }
          
          .company-details p {
            margin: 2px 0;
          }
          
          .quote-reference {
            min-width: 180px;
            text-align: right;
          }
          
          .quote-reference h2 {
            color: #C9A77C;
            font-size: 28px;
            margin-top: 0;
            margin-bottom: 10px;
          }
          
          .quote-reference p {
            margin: 3px 0;
          }
          
          .quote-client-info {
            background-color: #f8f8f8;
            padding: 10px 15px;
            border-radius: 4px;
            margin-bottom: 10px;
            page-break-inside: avoid;
          }
          
          .quote-client-info h3 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #444;
            font-size: 16px;
          }
          
          .quote-client-info p {
            margin: 3px 0;
          }
          
          .quote-details {
            margin-top: 10px;
            page-break-inside: avoid;
          }
          
          .quote-details h3 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #444;
            font-size: 16px;
          }
          
          .quote-details p {
            margin: 3px 0;
          }
          
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0 15px 0;
          }
          
          .items-table th {
            background-color: #444;
            color: white;
            padding: 8px 10px;
            text-align: left;
            font-size: 14px;
          }
          
          .items-table td {
            padding: 6px 10px;
            border-bottom: 1px solid #ddd;
            font-size: 13px;
            vertical-align: middle;
          }
          
          .items-table thead {
            display: table-header-group;
          }
          
          .items-table tbody {
            display: table-row-group;
          }
          
          .total-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
            page-break-inside: avoid;
          }
          
          .total-table td {
            padding: 8px 10px;
            font-size: 14px;
          }
          
          .total-table .total-label {
            text-align: right;
            font-weight: bold;
          }
          
          .total-table .total-value {
            text-align: right;
            width: 120px;
          }
          
          .total-row {
            background-color: #f0f0f0;
          }
          
          .deposit-row {
            color: #C9A77C;
          }
          
          .quote-terms {
            margin-top: 15px;
            padding: 10px 15px;
            background-color: #f8f8f8;
            border-radius: 4px;
            page-break-inside: avoid;
          }
          
          .quote-terms h3 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #444;
            font-size: 16px;
          }
          
          .quote-terms p {
            margin: 5px 0;
            font-size: 13px;
          }
          
          .text-right {
            text-align: right;
          }
          
          .text-center {
            text-align: center;
          }
          
          .delivery-row td, .washing-option-row td {
            background-color: #fafafa;
          }
        </style>
      </head>
      <body>
        <div class="quote-preview">
          <div class="quote-header-section">
            <div class="logo-container">
              <img src="${logoSrc}" alt="Logo" class="company-logo" />
            </div>
            <div class="company-details">
              <h3>${companyInfo.name}</h3>
              <p>${companyInfo.address}</p>
              <p>${companyInfo.zipCode} ${companyInfo.city}</p>
              <p>Tél: ${companyInfo.phone}</p>
              <p>Email: ${companyInfo.email}</p>
              <p>SIRET: ${companyInfo.siret}</p>
            </div>
            <div class="quote-reference">
              <h2>DEVIS</h2>
              <p><strong>N°:</strong> ${quoteData.quoteNumber}</p>
              <p><strong>Date:</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
              <p><strong>Validité:</strong> 30 jours</p>
            </div>
          </div>
          
          <div class="quote-client-info">
            <h3>Client</h3>
            <p><strong>Nom:</strong> ${quoteData.clientName}</p>
            <p><strong>Email:</strong> ${quoteData.email}</p>
            ${quoteData.deliveryMethod === 'delivery' && quoteData.deliveryAddress ? `
              <p><strong>Adresse de livraison:</strong></p>
              <p>${quoteData.deliveryAddress.street}</p>
              <p>${quoteData.deliveryAddress.zipCode} ${quoteData.deliveryAddress.city}</p>
            ` : ''}
          </div>
          
          <div class="quote-details">
            <h3>Détails du devis</h3>
            <p><strong>Période de location:</strong> ${new Date(quoteData.startDate).toLocaleDateString('fr-FR')} au ${new Date(quoteData.endDate).toLocaleDateString('fr-FR')}</p>
            <p><strong>Durée:</strong> ${quoteData.days || 1} jour${(quoteData.days || 1) > 1 ? 's' : ''}</p>
          </div>
          
          <!-- Tableau des articles avec en-tête répété sur chaque page -->
          <table class="items-table">
            <thead>
              <tr>
                <th>Désignation</th>
                <th>Quantité</th>
                <th>Prix unitaire</th>
                <th>Total HT</th>
                <th>TVA (${companyInfo.tvaRate}%)</th>
                <th>Total TTC</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>
          
          <!-- Tableau séparé pour les totaux qui ne s'affichera qu'une fois à la fin -->
          <table class="total-table">
            <tr>
              <td class="total-label">Sous-total HT</td>
              <td class="total-value">${subtotalHT.toFixed(2)} €</td>
            </tr>
            <tr>
              <td class="total-label">TVA (${companyInfo.tvaRate}%)</td>
              <td class="total-value">${totalTVA.toFixed(2)} €</td>
            </tr>
            <tr class="total-row">
              <td class="total-label">Total TTC</td>
              <td class="total-value">${totalTTC.toFixed(2)} €</td>
            </tr>
            ${showDeposit ? `
            <tr class="deposit-row">
              <td class="total-label">Acompte à payer (50%)</td>
              <td class="total-value">${deposit.toFixed(2)} €</td>
            </tr>
            ` : ''}
          </table>
          
          <div class="quote-terms">
            <h3>Conditions</h3>
            <p>Ce devis est valable 30 jours à compter de sa date d'émission.</p>
            ${showDeposit ? `
            <p>Un acompte de 50% du montant total est demandé pour confirmer la commande.</p>
            <p>Le solde devra être réglé à la livraison du matériel.</p>
            ` : `
            <p>Le paiement complet sera effectué à la livraison du matériel.</p>
            `}
          </div>
        </div>
      </body>
      </html>
    `;
    
    // Envoyer la requête pour télécharger le PDF
    const res = await fetch(`https://backend.ouest-pro-event.fr/admin/rentals/${quoteData.id}/download_quote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        html: completeHtml,
        delivery_distance: deliveryDistance,
        delivery_price: deliveryPrice,
        washing_option: quoteData.washingOption,
        washing_price: quoteData.washingPrice,
        show_deposit: showDeposit, // Ajouter cette option
        quote_data: {
          ...quoteData,
          showDeposit: showDeposit // Et aussi ici
        }
      })
    });
    
    // Si succès, récupérer le PDF et le télécharger
    if (res.ok) {
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Devis-${quoteData.quoteNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } else {
      const data = await res.json();
      alert("Erreur : " + data.error);
    }
  } catch (error) {
    console.error("Erreur lors du téléchargement du devis :", error);
    alert("Erreur lors du téléchargement du devis");
  }
};

  // Fonction pour mettre à jour les informations du devis
  const handleUpdateQuote = async () => {
    try {
      
      // Mise à jour des données dans la base de données
      const res = await fetch(`https://backend.ouest-pro-event.fr/admin/rentals/${quoteData.id}/update_quote`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        // body: JSON.stringify({ 
        //   items: quoteData.items.map(item => ({
        //     id: item.id, // Assurez-vous que chaque item a un ID
        //     quantity: item.quantity,
        //     daily_price: item.daily_price
        //   })),
        //   total_price: quoteData.totalPrice,
        //   delivery_distance: deliveryDistance,
        //   delivery_price: deliveryPrice,
        //   washing_option: quoteData.washingOption,
        //   washing_price: washingPrice

        body: JSON.stringify({
          items: quoteData.items.map(item => ({
            id: item.id, // Assurez-vous que chaque item a un ID
            quantity: item.quantity,
            daily_price: item.daily_price
          })),
          services: (quoteData.services || []).map(service => ({
            id: service.id,
            quantity: service.quantity,
            unit_price: service.unit_price,
            name: service.name
          })),
          total_price: quoteData.totalPrice,
          delivery_distance: deliveryDistance,
          delivery_price: deliveryPrice,
          washing_option: quoteData.washingOption,
          washing_price: washingPrice
        })

      });
      
      if (res.ok) {
        alert("Les modifications du devis ont été enregistrées.");
        setIsEditMode(false);
        
        // Rafraîchir les données
        fetchRentals(page);
      } else {
        const data = await res.json();
        alert("Erreur : " + data.error);
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du devis :", error);
      alert("Erreur lors de la mise à jour du devis");
    }
  };
  
  // Fonction pour modifier un élément du devis - MODIFIÉE: suppression du calcul avec les jours
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...quoteData.items];
    updatedItems[index][field] = parseFloat(value);
    
    // Recalculer le sous-total pour cet article - MODIFIÉ: suppression du calcul avec les jours
    updatedItems[index].subtotal = updatedItems[index].quantity * updatedItems[index].daily_price;
    
    // // Recalculer le sous-total général des articles
    // const newSubtotal = updatedItems.reduce((total, item) => total + item.subtotal, 0);
    
    // // Calculer le total HT (articles + livraison + option lavage)

    const productsSubtotal = updatedItems.reduce((total, item) => total + item.subtotal, 0);
    const servicesSubtotal = (quoteData.services || []).reduce((total, service) => total + (service.subtotal || 0), 0);
    const newSubtotal = productsSubtotal + servicesSubtotal;

    // Calculer le total HT (articles + prestations + livraison + option lavage)

    const totalHT = newSubtotal + deliveryPrice + (quoteData.washingOption ? washingPrice : 0);
    
    // Recalculer la TVA
    const totalTVA = totalHT * (companyInfo.tvaRate / 100);
    
    // Recalculer le total général
    const totalTTC = totalHT + totalTVA;
    
    setQuoteData({
      ...quoteData,
      items: updatedItems,
      subtotal: newSubtotal,
      totalPrice: totalTTC
    });
  };


  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...(quoteData.services || [])];
    if (!updatedServices[index]) {
      return;
    }

    if (field === 'quantity') {
      const parsedQuantity = parseInt(value, 10);
      if (!Number.isNaN(parsedQuantity) && parsedQuantity >= 0) {
        updatedServices[index].quantity = parsedQuantity;
      }
    } else if (field === 'unit_price') {
      const parsedPrice = parseFloat(value);
      if (!Number.isNaN(parsedPrice) && parsedPrice >= 0) {
        updatedServices[index].unit_price = parsedPrice;
      }
    } else {
      updatedServices[index][field] = value;
    }

    const quantity = updatedServices[index].quantity || 0;
    const unitPrice = updatedServices[index].unit_price || 0;
    updatedServices[index].subtotal = quantity * unitPrice;

    const productsSubtotal = quoteData.items.reduce((total, item) => total + item.subtotal, 0);
    const servicesSubtotal = updatedServices.reduce((total, service) => total + (service.subtotal || 0), 0);
    const newSubtotal = productsSubtotal + servicesSubtotal;

    const totalHT = newSubtotal + deliveryPrice + (quoteData.washingOption ? washingPrice : 0);
    const totalTVA = totalHT * (companyInfo.tvaRate / 100);
    const totalTTC = totalHT + totalTVA;

    setQuoteData({
      ...quoteData,
      services: updatedServices,
      subtotal: newSubtotal,
      totalPrice: totalTTC
    });
  };
  
  // Gérer le changement de l'option de lavage
  const handleWashingOptionChange = () => {
    const newWashingOption = !quoteData.washingOption;
    const currentWashingPrice = newWashingOption ? washingPrice : 0;
    
    // Recalculer le prix total avec ou sans option lavage
    const totalHT = quoteData.subtotal + deliveryPrice + currentWashingPrice;
    const totalTVA = totalHT * (companyInfo.tvaRate / 100);
    const totalTTC = totalHT + totalTVA;
    
    setQuoteData({
      ...quoteData,
      washingOption: newWashingOption,
      washingPrice: currentWashingPrice,
      totalPrice: totalTTC
    });
  };

  const handleWashingPriceChange = (newPrice) => {
    const price = parseFloat(newPrice);
    setWashingPrice(price);
    
    if (quoteData.washingOption) {
      // Recalculer le total avec le nouveau prix de lavage
      const totalHT = quoteData.subtotal + deliveryPrice + price;
      const totalTVA = totalHT * (companyInfo.tvaRate / 100);
      const totalTTC = totalHT + totalTVA;
      
      setQuoteData({
        ...quoteData,
        washingPrice: price, // Stocker le prix du lavage dans quoteData
        totalPrice: totalTTC
      });
    }
  };
  
  // Formater la date au format français
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  // Gestion de la pagination
  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  if (loading) {
    return <div>Chargement...</div>;
  }

  return (
    <div className="admin-rentals-page">
      <h1>Gestion des Locations</h1>
      <div className="status-legend">
        <h3>Légende des statuts</h3>
        <div className="legend-item">
          <div className="legend-color" data-status="en attente"></div>
          <span>En attente</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" data-status="devis envoyé par mail"></div>
          <span>Devis envoyé</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" data-status="paid"></div>
          <span>Acompte payé</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" data-status="décliner"></div>
          <span>Décliné par client</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" data-status="facture envoyée"></div>
          <span>Facture envoyée</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" data-status="refused"></div>
          <span>Refusé par admin</span>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Client</th>
            <th>Email</th>
            <th>Période</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rentals.map(rental => (
            <tr key={rental.id} onClick={() => openDetailsModal(rental)} data-status={rental.status.toLowerCase()}>
              <td>{rental.id}</td>
              <td>{rental.first_name} {rental.last_name}</td>
              <td>{rental.email}</td>
              <td>{formatDate(rental.start_date)} - {formatDate(rental.end_date)}</td>
              <td data-status={rental.status.toLowerCase()}>{rental.status}</td>
              <td onClick={e => e.stopPropagation()}>
                <button style={{ 
                  backgroundColor: '#3f51b5', 
                  color: 'white', 
                  padding: '8px 12px',
                  margin: '3px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }} onClick={() => openQuoteModal(rental)}>
                  Voir devis
                </button>
                
                <button style={{ 
                  backgroundColor: '#3f51b5', 
                  color: 'white', 
                  padding: '8px 12px',
                  margin: '3px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }} onClick={() => openInvoiceModal(rental)}>
                  Voir facture
                </button>
                
                <button 
                  style={{ 
                    backgroundColor: '#3f51b5', 
                    color: 'white', 
                    padding: '8px 12px',
                    margin: '3px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }} 
                  onClick={() => {
                    if (rental.status === 'en attente' || rental.status === 'devis envoyé par mail') {
                      setSelectedRental(rental);
                      setIsRefusalModalOpen(true);
                    } else {
                      alert("Cette location ne peut plus être refusée car son statut est : " + rental.status);
                    }
                  }}
                >
                  Refuser
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="pagination">
        <button onClick={handlePrevPage} disabled={page === 1}>Précédent</button>
        <span>Page {page} sur {totalPages}</span>
        <button onClick={handleNextPage} disabled={page === totalPages}>Suivant</button>
      </div>

      {/* Modale de détails */}
      {isDetailsModalOpen && selectedRental && (
        <div className="modal-overlay" onClick={closeDetailsModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Détails de la Location #{selectedRental.id}</h2>
              <button onClick={closeDetailsModal}>×</button>
            </div>
            <div className="modal-content">
              <h3>Informations client</h3>
              <div className="details-section">
                <p><strong>Nom:</strong> {selectedRental.first_name} {selectedRental.last_name}</p>
                <p><strong>Email:</strong> {selectedRental.email}</p>
                <p><strong>Téléphone:</strong> {selectedRental.phone || "Non renseigné"}</p>
              </div>
              
              <h3>Détails de la location</h3>
              <div className="details-section">
                <p><strong>Période:</strong> {formatDate(selectedRental.start_date)} - {formatDate(selectedRental.end_date)}</p>
                <p><strong>Statut:</strong> <span className="status-badge" data-status={selectedRental.status.toLowerCase()}>{selectedRental.status}</span></p>
                <p><strong>Mode de livraison:</strong> {selectedRental.delivery_method === 'delivery' ? 'Livraison' : 'Récupération sur place'}</p>
                
                {selectedRental.delivery_method === 'delivery' && (
                  <div className="delivery-details">
                    <p><strong>Adresse de livraison:</strong></p>
                    <p>{selectedRental.delivery_street}</p>
                    <p>{selectedRental.delivery_zip_code} {selectedRental.delivery_city}</p>
                    {selectedRental.delivery_distance > 0 && (
                      <p><strong>Distance:</strong> {selectedRental.delivery_distance} km (Prix: {selectedRental.delivery_price} €)</p>
                    )}
                  </div>
                )}
                
                {/* Afficher l'option de lavage si activée */}
                {selectedRental.washing_option && (
                  <div className="washing-option-details">
                    <p><strong>Forfait lavage:</strong> Oui ({washingPrice} €)</p>
                  </div>
                )}
              </div>
              
              <h3>Articles loués</h3>
              <table className="details-items-table">
                <thead>
                  <tr>
                    <th>Désignation</th>
                    <th>Quantité</th>
                    <th>Prix unitaire</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedRental.items.map((item, index) => {
                    // MODIFIÉ: suppression du calcul avec les jours
                    const itemTotal = item.quantity * item.daily_price;
                    return (
                      <tr key={index}>
                        <td>{item.product_name}</td>
                        <td className="text-center">{item.quantity}</td>
                        <td className="text-right">{item.daily_price.toFixed(2)} €</td>
                        <td className="text-right">{itemTotal.toFixed(2)} €</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              <div className="price-summary">
                {(() => {
                  // Calculer le nombre de jours
                  const startDate = new Date(selectedRental.start_date);
                  const endDate = new Date(selectedRental.end_date);
                  const diffTime = Math.abs(endDate - startDate);
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                  
                  // Calculer le sous-total des articles - MODIFIÉ: suppression du calcul avec les jours
                  const articlesSubtotal = selectedRental.items.reduce((sum, item) => 
                    sum + (item.quantity * item.daily_price), 0);
                  
                  // Ajouter les frais de livraison s'ils existent
                  const deliveryPrice = selectedRental.delivery_price || 0;
                  
                  // Ajouter le forfait lavage s'il est activé
                  const washingCost = selectedRental.washing_option ? (selectedRental.washing_price || washingPrice) : 0;
                  
                  // Calculer le total HT
                  const totalHT = articlesSubtotal + deliveryPrice + washingCost;
                  
                  // Calculer la TVA
                  const tvaAmount = totalHT * (companyInfo.tvaRate / 100);
                  
                  // Calculer le total TTC
                  const totalTTC = totalHT + tvaAmount;
                  
                  return (
                    <>
                      <p><strong>Total HT:</strong> {totalHT.toFixed(2)} €</p>
                      <p><strong>TVA ({companyInfo.tvaRate}%):</strong> {tvaAmount.toFixed(2)} €</p>
                      <p><strong>Total TTC:</strong> {totalTTC.toFixed(2)} €</p>
                    </>
                  );
                })()}
              </div>
              
              {selectedRental.status.toLowerCase() === 'refused' && (
                <div className="refusal-details">
                  <h3>Motif du refus</h3>
                  <p>{selectedRental.refusal_message || "Aucun motif spécifié"}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {getAvailableActions(selectedRental.status).canSendQuote && (
                <button onClick={() => openQuoteModal(selectedRental)} className="quote-btn">Préparer un devis</button>
              )}
              {getAvailableActions(selectedRental.status).canRefuse && (
                <button onClick={() => {
                  setIsRefusalModalOpen(true);
                  closeDetailsModal();
                }} className="refuse-btn">Refuser la demande</button>
              )}
              {getAvailableActions(selectedRental.status).canSendInvoice && (
                <button onClick={() => openInvoiceModal(selectedRental)} className="invoice-btn">Générer une facture</button>
              )}
              <button onClick={closeDetailsModal}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale de refus */}
      {isRefusalModalOpen && selectedRental && (
        <div className="modal-overlay" onClick={closeRefusalModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Refuser la location #{selectedRental.id}</h2>
              <button onClick={closeRefusalModal}>×</button>
            </div>
            <div className="modal-content">
              <div className="refusal-info">
                <h3>Client</h3>
                <p><strong>Nom:</strong> {selectedRental.first_name} {selectedRental.last_name}</p>
                <p><strong>Email:</strong> {selectedRental.email}</p>
                
                <h3>Détails de la location</h3>
                <p><strong>Période:</strong> {formatDate(selectedRental.start_date)} - {formatDate(selectedRental.end_date)}</p>
                <p><strong>Articles:</strong> {selectedRental.items.length} article(s)</p>
              </div>
              
              <div className="refusal-message">
                <label htmlFor="refusal-reason">
                  <strong>Motif du refus</strong>
                  <span className="required"> (obligatoire)</span>
                </label>
                <p className="help-text">Ce message sera envoyé par email au client pour expliquer le refus de sa demande de location.</p>
                <textarea 
                  id="refusal-reason"
                  value={refusalMessage}
                  onChange={(e) => setRefusalMessage(e.target.value)}
                  placeholder="Veuillez expliquer la raison du refus..."
                  rows={5}
                  required
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                onClick={() => {
                  if (refusalMessage.trim() === '') {
                    alert("Veuillez indiquer un motif de refus.");
                    return;
                  }
                  handleRefuseAction(selectedRental.id, refusalMessage);
                }}
                className="refuse-confirm-btn"
                disabled={refusalMessage.trim() === ''}
              >
                Confirmer le refus
              </button>
              <button onClick={closeRefusalModal} className="cancel-btn">Annuler</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modale de devis améliorée */}
      {isQuoteModalOpen && quoteData && (
        <div className="modal-overlay" onClick={closeQuoteModal}>
          <div className="modal quote-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Devis N°{quoteData.quoteNumber}</h2>
              <button onClick={closeQuoteModal}>×</button>
            </div>
            
            <div className="modal-content">
              {/* Options d'édition */}
              {isEditMode && (
                <div className="edit-options">
                  <h4>Options supplémentaires</h4>
                  <label className="option-checkbox">
                    <input
                      type="checkbox"
                      checked={quoteData.washingOption}
                      onChange={handleWashingOptionChange}
                    />
                    <div className="option-label">
                      <div className="option-title">Forfait lavage</div>
                      <p className="option-description">Nettoyage complet du matériel après location</p>
                    </div>
                    <div className="option-price">
                      <input 
                        type="number" 
                        step="0.01" 
                        min="0" 
                        value={washingPrice} 
                        onChange={(e) => handleWashingPriceChange(e.target.value)}
                        className="small-input"
                      /> €
                    </div>
                  </label>
                </div>
              )}
              
              {/* Prévisualisation du devis (ce qui sera exporté en PDF) */}
              <div ref={quoteRef} className="quote-preview">
                <div className="quote-header-section">
                  <div className="quote-company-info">
                    <img src={companyLogo} alt="Logo" className="company-logo" />
                    <div className="company-details">
                      <h3>{companyInfo.name}</h3>
                      <p>{companyInfo.address}</p>
                      <p>{companyInfo.zipCode} {companyInfo.city}</p>
                      <p>Tél: {companyInfo.phone}</p>
                      <p>Email: {companyInfo.email}</p>
                      <p>SIRET: {companyInfo.siret}</p>
                    </div>
                  </div>
                  
                  <div className="quote-reference">
                    <h2>DEVIS</h2>
                    <p><strong>N°:</strong> {quoteData.quoteNumber}</p>
                    <p><strong>Date:</strong> {new Date().toLocaleDateString('fr-FR')}</p>
                    <p><strong>Validité:</strong> 30 jours</p>
                  </div>
                </div>
                
                <div className="quote-client-info">
                  <h3>Client</h3>
                  <p><strong>Nom:</strong> {quoteData.clientName}</p>
                  <p><strong>Email:</strong> {quoteData.email}</p>
                  
                  {quoteData.deliveryMethod === 'delivery' && quoteData.deliveryAddress && (
                    <div>
                      <p><strong>Adresse de livraison:</strong></p>
                      <p>{quoteData.deliveryAddress.street}</p>
                      <p>{quoteData.deliveryAddress.zipCode} {quoteData.deliveryAddress.city}</p>
                    </div>
                  )}
                </div>
                
                <div className="quote-details">
                  <h3>Détails du devis</h3>
                  <p><strong>Période de location:</strong> {formatDate(quoteData.startDate)} au {formatDate(quoteData.endDate)}</p>
                  <p><strong>Durée:</strong> {quoteData.days} jour{quoteData.days > 1 ? 's' : ''}</p>
                  
                  <table className="quote-items-table">
                    <thead>
                      <tr>
                        <th>Désignation</th>
                        <th>Quantité</th>
                        <th>Prix unitaire</th>
                        <th>Total HT</th>
                        <th>TVA ({companyInfo.tvaRate}%)</th>
                        <th>Total TTC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quoteData.items.map((item, index) => {
                        // Calculs pour chaque élément
                        const itemTvaAmount = item.subtotal * (companyInfo.tvaRate / 100);
                        const itemTotalTTC = item.subtotal + itemTvaAmount;
                        
                        return (
                          <tr key={index}>
                            <td>{item.product_name}</td>
                            <td className="text-center">
                              {isEditMode ? (
                                <input 
                                  type="number" 
                                  min="1" 
                                  value={item.quantity} 
                                  onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                />
                              ) : (
                                item.quantity
                              )}
                            </td>
                            <td className="text-right">
                              {isEditMode ? (
                                <input 
                                  type="number" 
                                  step="0.01" 
                                  min="0" 
                                  value={item.daily_price} 
                                  onChange={(e) => handleItemChange(index, 'daily_price', e.target.value)}
                                />
                              ) : (
                                `${item.daily_price.toFixed(2)} €`
                              )}
                            </td>
                            <td className="text-right">{item.subtotal.toFixed(2)} €</td>
                            <td className="text-right">{itemTvaAmount.toFixed(2)} €</td>
                            <td className="text-right">{itemTotalTTC.toFixed(2)} €</td>
                          </tr>
                        );
                      })}


{(quoteData.services || []).map((service, serviceIndex) => {
                        const serviceLabel = service.name || service.formula_item_title || 'Prestation';
                        const serviceSubtotal = service.subtotal || 0;
                        const serviceTvaAmount = serviceSubtotal * (companyInfo.tvaRate / 100);
                        const serviceTotalTTC = serviceSubtotal + serviceTvaAmount;

                        return (
                          <tr key={`service-${service.id || serviceIndex}`}>
                            <td>
                              {serviceLabel}
                              {service.formula_name && (
                                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                  {service.formula_name}
                                </div>
                              )}
                            </td>
                            <td className="text-center">
                              {isEditMode ? (
                                <input
                                  type="number"
                                  min="0"
                                  value={service.quantity ?? 0}
                                  onChange={(e) => handleServiceChange(serviceIndex, 'quantity', e.target.value)}
                                />
                              ) : (
                                service.quantity ?? 0
                              )}
                            </td>
                            <td className="text-right">
                              {isEditMode ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={service.unit_price || 0}
                                  onChange={(e) => handleServiceChange(serviceIndex, 'unit_price', e.target.value)}
                                />
                              ) : (
                                `${(service.unit_price || 0).toFixed(2)} €`
                              )}
                            </td>
                            <td className="text-right">{serviceSubtotal.toFixed(2)} €</td>
                            <td className="text-right">{serviceTvaAmount.toFixed(2)} €</td>
                            <td className="text-right">{serviceTotalTTC.toFixed(2)} €</td>
                          </tr>
                        );
                      })}
                      
                      {/* Ligne pour la livraison si applicable (aller-retour) */}
                      {quoteData.deliveryMethod === 'delivery' && (
                        <tr className="delivery-row">
                          <td>
                            {isEditMode ? (
                              <div className="delivery-edit-inline">
                                Frais de livraison aller-retour (
                                <input 
                                  type="number" 
                                  min="0" 
                                  step="1" 
                                  value={deliveryDistance} 
                                  onChange={(e) => {
                                    const newDistance = parseFloat(e.target.value);
                                    setDeliveryDistance(newDistance);
                                    // Calculer le prix unitaire actuel (ou utiliser la valeur par défaut 1.5)
                                    const pricePerKm = deliveryDistance > 0 ? deliveryPrice / (deliveryDistance * 2) : 1.5;
                                    // Recalculer le prix de livraison avec le prix unitaire actuel
                                    const newDeliveryPrice = newDistance * 2 * pricePerKm;
                                    setDeliveryPrice(newDeliveryPrice);
                                    
                                    // Mettre à jour le total avec le nouveau prix de livraison et l'option lavage
                                    const totalHT = quoteData.subtotal + newDeliveryPrice + (quoteData.washingOption ? quoteData.washingPrice : 0);
                                    const totalTVA = totalHT * (companyInfo.tvaRate / 100);
                                    const totalTTC = totalHT + totalTVA;
                                    
                                    setQuoteData({
                                      ...quoteData,
                                      deliveryPrice: newDeliveryPrice,
                                      totalPrice: totalTTC
                                    });
                                  }}
                                  className="small-input"
                                /> km)
                                {loading2 && <span className="spinner-inline"></span>}
                              </div>
                            ) : (
                              `Frais de livraison aller-retour (${deliveryDistance} km)`
                            )}
                          </td>
                          <td className="text-center">2</td>
                          <td className="text-right">
                            {isEditMode ? (
                              <input 
                                type="number" 
                                step="0.01" 
                                min="0" 
                                value={(deliveryPrice / 2).toFixed(2)} 
                                onChange={(e) => {
                                  const pricePerKm = parseFloat(e.target.value);
                                  const newDeliveryPrice = deliveryDistance * 2 * pricePerKm;
                                  
                                  setDeliveryPrice(newDeliveryPrice);
                                  
                                  // Mettre à jour le total avec le nouveau prix de livraison
                                  const totalHT = quoteData.subtotal + newDeliveryPrice + (quoteData.washingOption ? quoteData.washingPrice : 0);
                                  const totalTVA = totalHT * (companyInfo.tvaRate / 100);
                                  const totalTTC = totalHT + totalTVA;
                                  
                                  setQuoteData({
                                    ...quoteData,
                                    deliveryPrice: newDeliveryPrice,
                                    totalPrice: totalTTC
                                  });
                                }}
                                className="small-input"
                              />
                            ) : (
                              `${(deliveryPrice / 2).toFixed(2)} €`
                            )}
                          </td>
                          <td className="text-right">{deliveryPrice.toFixed(2)} €</td>
                          <td className="text-right">{(deliveryPrice * companyInfo.tvaRate / 100).toFixed(2)} €</td>
                          <td className="text-right">{(deliveryPrice * (1 + companyInfo.tvaRate / 100)).toFixed(2)} €</td>
                        </tr>
                      )}
                      
                      {/* Nouvelle ligne pour le forfait lavage si activé */}
                      {quoteData.washingOption && (
                        <tr className="washing-option-row">
                          <td>Forfait lavage</td>
                          <td className="text-center">1</td>
                          <td className="text-right">{quoteData.washingPrice.toFixed(2)} €</td>
                          <td className="text-right">{quoteData.washingPrice.toFixed(2)} €</td>
                          <td className="text-right">{(quoteData.washingPrice * companyInfo.tvaRate / 100).toFixed(2)} €</td>
                          <td className="text-right">{(quoteData.washingPrice * (1 + companyInfo.tvaRate / 100)).toFixed(2)} €</td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3" className="text-right">Sous-total HT</td>
                        <td className="text-right">
                          {(quoteData.subtotal + deliveryPrice + (quoteData.washingOption ? quoteData.washingPrice : 0)).toFixed(2)} €
                        </td>
                        <td colSpan="2"></td>
                      </tr>
                      <tr>
                        <td colSpan="3" className="text-right">TVA ({companyInfo.tvaRate}%)</td>
                        <td colSpan="1"></td>
                        <td className="text-right">
                          {((quoteData.subtotal + deliveryPrice + (quoteData.washingOption ? quoteData.washingPrice : 0)) * companyInfo.tvaRate / 100).toFixed(2)} €
                        </td>
                        <td colSpan="1"></td>
                      </tr>
                      <tr className="total-row">
                        <td colSpan="5" className="text-right">Total TTC</td>
                        <td className="text-right">
                          {((quoteData.subtotal + deliveryPrice + (quoteData.washingOption ? quoteData.washingPrice : 0)) * (1 + companyInfo.tvaRate / 100)).toFixed(2)} €
                        </td>
                      </tr>
                      {showDeposit && (
                        <tr className="deposit-row">
                          <td colSpan="5" className="text-right">Acompte à payer (50%)</td>
                          <td className="text-right">
                            {(((quoteData.subtotal + deliveryPrice + (quoteData.washingOption ? quoteData.washingPrice : 0)) * (1 + companyInfo.tvaRate / 100)) * 0.5).toFixed(2)} €
                          </td>
                        </tr>
                      )}
                    </tfoot>
                  </table>
                </div>
                
                <div className="quote-terms">
                  <h3>Conditions</h3>
                  <p>Ce devis est valable 30 jours à compter de sa date d'émission.</p>
                  <p>Un acompte de 50% du montant total est demandé pour confirmer la commande.</p>
                  <p>Le solde devra être réglé à la livraison du matériel.</p>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              {isEditMode ? (
                <>
                  <button onClick={handleUpdateQuote} className="save-btn">Enregistrer les modifications</button>
                  <button onClick={() => setIsEditMode(false)} className="cancel-btn">Annuler</button>
                </>
              ) : (
                <>
                  <div className="modal-footer-content">
                    <div className="email-message-section">
                      <h3>Message pour l'email</h3>
                      <textarea
                        value={quoteEmailMessage}
                        onChange={(e) => setQuoteEmailMessage(e.target.value)}
                        placeholder="Ajoutez ici un message personnalisé qui sera inclus dans l'email au client..."
                        rows={4}
                        className="email-message-input"
                      ></textarea>
                    </div>

                    {/* Option pour afficher/masquer l'acompte */}
                    <label className="option-checkbox">
                      <input
                        type="checkbox"
                        checked={showDeposit}
                        onChange={() => setShowDeposit(!showDeposit)}
                      />
                      <div className="option-label">
                        <div className="option-title">Afficher les lignes d'acompte</div>
                        <p className="option-description">Afficher ou masquer les lignes d'acompte sur la facture</p>
                      </div>
                    </label>
                    
                    <div className="action-buttons">
                      <button onClick={() => setIsEditMode(true)} className="edit-btn">Modifier</button>
                      <button onClick={handleDownloadQuote} className="download-btn">Télécharger PDF</button>
                      <button onClick={() => handleGenerateAndSendQuote(quoteData.id)} className="send-btn">Envoyer par email</button>
                      <button onClick={closeQuoteModal} className="close-btn">Fermer</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modale de facture */}
      {isInvoiceModalOpen && invoiceData && (
        <div className="modal-overlay" onClick={closeInvoiceModal}>
          <div className="modal invoice-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Facture N°{invoiceData.invoiceNumber}</h2>
              <button onClick={closeInvoiceModal}>×</button>
            </div>
            
            <div className="modal-content">
              {/* Prévisualisation de la facture */}
              <div ref={invoiceRef} className="invoice-preview">
                <div className="invoice-header-section">
                  <div className="invoice-company-info">
                    <img src={companyLogo} alt="Logo" className="company-logo" />
                    <div className="company-details">
                      <h3>{companyInfo.name}</h3>
                      <p>{companyInfo.address}</p>
                      <p>{companyInfo.zipCode} {companyInfo.city}</p>
                      <p>Tél: {companyInfo.phone}</p>
                      <p>Email: {companyInfo.email}</p>
                      <p>SIRET: {companyInfo.siret}</p>
                    </div>
                  </div>
                  
                  <div className="invoice-reference">
                    <h2>FACTURE</h2>
                    <p><strong>N°:</strong> {invoiceData.invoiceNumber}</p>
                    <p><strong>Date:</strong> {new Date().toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>
                
                <div className="invoice-client-info">
                  <h3>Client</h3>
                  <p><strong>Nom:</strong> {invoiceData.clientName}</p>
                  <p><strong>Email:</strong> {invoiceData.email}</p>
                  
                  {invoiceData.deliveryMethod === 'delivery' && invoiceData.deliveryAddress && (
                    <div>
                      <p><strong>Adresse de livraison:</strong></p>
                      <p>{invoiceData.deliveryAddress.street}</p>
                      <p>{invoiceData.deliveryAddress.zipCode} {invoiceData.deliveryAddress.city}</p>
                    </div>
                  )}
                </div>
                
                <div className="invoice-details">
                  <h3>Détails de la facture</h3>
                  <p><strong>Période de location:</strong> {formatDate(invoiceData.startDate)} au {formatDate(invoiceData.endDate)}</p>
                  <p><strong>Durée:</strong> {invoiceData.days} jour{invoiceData.days > 1 ? 's' : ''}</p>
                  
                  <table className="invoice-items-table">
                    <thead>
                      <tr>
                        <th style={{color: '#000000'}}>Désignation</th>
                        <th style={{color: '#000000'}}>Quantité</th>
                        <th style={{color: '#000000'}}>Prix unitaire</th>
                        <th style={{color: '#000000'}}>Total HT</th>
                        <th style={{color: '#000000'}}>TVA ({companyInfo.tvaRate}%)</th>
                        <th style={{color: '#000000'}}>Total TTC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData.items.map((item, index) => {
                        // Calculs pour chaque élément
                        const itemTvaAmount = item.subtotal * (companyInfo.tvaRate / 100);
                        const itemTotalTTC = item.subtotal + itemTvaAmount;
                        
                        return (
                          <tr key={index}>
                            <td>{item.product_name}</td>
                            <td className="text-center">{item.quantity}</td>
                            <td className="text-right">{item.daily_price.toFixed(2)} €</td>
                            <td className="text-right">{item.subtotal.toFixed(2)} €</td>
                            <td className="text-right">{itemTvaAmount.toFixed(2)} €</td>
                            <td className="text-right">{itemTotalTTC.toFixed(2)} €</td>
                          </tr>
                        );
                      })}

{(invoiceData.services || []).map((service, serviceIndex) => {
                        const serviceLabel = service.name || service.formula_item_title || 'Prestation';
                        const fullLabel = service.formula_name ? `${serviceLabel} (${service.formula_name})` : serviceLabel;
                        const serviceSubtotal = service.subtotal || 0;
                        const serviceTvaAmount = serviceSubtotal * (companyInfo.tvaRate / 100);
                        const serviceTotalTTC = serviceSubtotal + serviceTvaAmount;

                        return (
                          <tr key={`invoice-service-${service.id || serviceIndex}`}>
                            <td>{fullLabel}</td>
                            <td className="text-center">{service.quantity ?? 0}</td>
                            <td className="text-right">{(service.unit_price || 0).toFixed(2)} €</td>
                            <td className="text-right">{serviceSubtotal.toFixed(2)} €</td>
                            <td className="text-right">{serviceTvaAmount.toFixed(2)} €</td>
                            <td className="text-right">{serviceTotalTTC.toFixed(2)} €</td>
                          </tr>
                        );
                      })}
                      
                      {/* Ligne pour la livraison si applicable */}
                      {invoiceData.deliveryMethod === 'delivery' && (
                        <tr className="delivery-row">
                          <td>Frais de livraison aller-retour ({invoiceData.deliveryDistance} km)</td>
                          <td className="text-center">2</td>
                          <td className="text-right">{(invoiceData.deliveryPrice / 2).toFixed(2)} €</td>
                          <td className="text-right">{invoiceData.deliveryPrice.toFixed(2)} €</td>
                          <td className="text-right">{(invoiceData.deliveryPrice * companyInfo.tvaRate / 100).toFixed(2)} €</td>
                          <td className="text-right">{(invoiceData.deliveryPrice * (1 + companyInfo.tvaRate / 100)).toFixed(2)} €</td>
                        </tr>
                      )}
                      
                      {/* Ligne pour le forfait lavage si activé */}
                      {invoiceData.washingOption && (
                        <tr className="washing-option-row">
                          <td>Forfait lavage</td>
                          <td className="text-center">1</td>
                          <td className="text-right">{invoiceData.washingPrice.toFixed(2)} €</td>
                          <td className="text-right">{invoiceData.washingPrice.toFixed(2)} €</td>
                          <td className="text-right">{(invoiceData.washingPrice * companyInfo.tvaRate / 100).toFixed(2)} €</td>
                          <td className="text-right">{(invoiceData.washingPrice * (1 + companyInfo.tvaRate / 100)).toFixed(2)} €</td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3" className="text-right">Sous-total HT</td>
                        <td className="text-right">
                        {(invoiceData.subtotal + invoiceData.deliveryPrice + (invoiceData.washingOption ? invoiceData.washingPrice : 0)).toFixed(2)} €
                        </td>
                        <td colSpan="2"></td>
                      </tr>
                      <tr>
                        <td colSpan="3" className="text-right">TVA ({companyInfo.tvaRate}%)</td>
                        <td colSpan="1"></td>
                        {console.log('test : ', invoiceData)}
                        <td className="text-right">{invoiceData.tvaAmount.toFixed(2)} €</td>
                        <td colSpan="1"></td>
                      </tr>
                      <tr className="total-row">
                        <td colSpan="5" className="text-right">Total TTC</td>
                        <td className="text-right">{invoiceData.totalPrice.toFixed(2)} €</td>
                      </tr>
                      {/* Ligne pour l'acompte déjà payé */}
                      {showDeposit && (
                        <tr className="paid-deposit-row">
                          <td colSpan="5" className="text-right">Acompte déjà payé (50%)</td>
                          <td className="text-right">-{(invoiceData.totalPrice * 0.5).toFixed(2)} €</td>
                        </tr>
                      )}
                      {/* Ligne pour le solde restant à payer */}
                      {showDeposit && (
                        <tr className="remaining-amount-row">
                          <td colSpan="5" className="text-right"><strong>Reste à payer</strong></td>
                          <td className="text-right"><strong>{((invoiceData.subtotal + invoiceData.deliveryPrice + (invoiceData.washingOption ? invoiceData.washingPrice : 0)) * (1 + companyInfo.tvaRate / 100) * 0.5).toFixed(2)} €</strong></td>
                        </tr>
                      )}
                    </tfoot>
                  </table>
                </div>
                
                <div className="invoice-terms">
                  <h3>Informations de paiement</h3>
                  <p>Un acompte de 50% ({(invoiceData.totalPrice * 0.5).toFixed(2)} €) a été versé.</p>
                  <p>Le solde de {(invoiceData.totalPrice * 0.5).toFixed(2)} € est à régler lors de la récupération du matériel.</p>
                  <p>Merci pour votre confiance.</p>
                </div>
              </div>
            </div>

            <div className="email-message-section">
              <h3>Message pour l'email</h3>
              <textarea
                value={invoiceEmailMessage}
                onChange={(e) => setInvoiceEmailMessage(e.target.value)}
                placeholder="Ajoutez ici un message personnalisé qui sera inclus dans l'email au client..."
                rows={4}
                className="email-message-input"
              ></textarea>
            </div>

            {/* Option pour afficher/masquer l'acompte */}
            <div className="invoice-options">
              <label className="option-checkbox">
                <input
                  type="checkbox"
                  checked={showDeposit}
                  onChange={() => setShowDeposit(!showDeposit)}
                />
                <div className="option-label">
                  <div className="option-title">Afficher les lignes d'acompte</div>
                  <p className="option-description">Afficher ou masquer les lignes d'acompte sur la facture</p>
                </div>
              </label>
            </div>
            
            <div className="modal-footer">
              <button onClick={handleDownloadInvoice} className="download-btn">Télécharger PDF</button>
              <button onClick={() => handleGenerateAndSendInvoice(invoiceData.id)} className="send-btn">Envoyer par email</button>
              <button onClick={closeInvoiceModal} className="close-btn">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminRentalsPage;